import { Location } from '@angular/common';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import { isEqual } from 'lodash-es';
import {
    BehaviorSubject,
    combineLatest,
    defer,
    EMPTY,
    Observable,
    Observer,
    of,
    ReplaySubject,
    Subject,
    throwError
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    shareReplay,
    skip,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import {
    ActionItem,
    ActionItemStyle,
    ApiQuery,
    Combine,
    DialogStyle,
    EllipsisMultiActionItem,
    EmptyStructuredQuery,
    FacetProfile,
    FacetSelectionType,
    FacetsToQueryParts,
    FieldDefinition,
    MergeStructuredQueries,
    Operand,
    ParamField,
    QueryPart,
    QueryPartData,
    QueryPartStrategy,
    Restriction,
    ShBooleanDialogComponent,
    ShDynamicDialogService,
    ShFacet,
    ShFacetBodyContext,
    ShFacetDataService,
    ShSelectedFacetValue,
    ShUrlQueryService,
    SortDirection,
    SortInfo,
    SortOption,
    StructuredQuery,
    Toast,
    UrlQueryParams,
    UrlQueryParamsConverter
} from '@stylehaven/components';
import { ColumnManagementService } from '../../../column-management/column-management.service';
import { AbstractRecordOverviewContainerComponent } from '../../../containers/abstract-record-overview-container.component';
import { classifyCreateFailure, toFailureBanner } from '../../../downloads/helpers/export-failure.helper';
import {
    AnalogExportChoice,
    AnalogExportDialogComponent,
    AnalogExportDialogData
} from '../../../downloads/components/analog-export-dialog/analog-export-dialog.component';
import {
    QuickExportSandboxService,
    toQuickExportContentMode
} from '../../../downloads/sandbox/quick-export-sandbox.service';
import {
    filterPanelForViewport,
    FilterPanelState
} from '../../../elements/components/filter-panel-toggle/filter-panel.state';
import { ExportFailureBanner } from '../../../entities/downloads/export-failure.entity';
import { ExportScope } from '../../../entities/downloads/export-scope.entity';
import { defaultNavigationOptions } from '../../../entities/navigation/navigation-options.entity';
import { NavigationQuery } from '../../../entities/navigation/navigation-query.entity';
import { NotificationItem } from '../../../entities/notifications/notification-item.entity';
import { Organisation } from '../../../entities/organisation/organisation.entity';
import {
    LoadSavedQueryDialogOutput,
    LoadSavedQueryDialogOutputSuccess
} from '../../../entities/saved-queries/load-saved-query-dialog.entity';
import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';
import { ExportStartPayload } from '../../../export-sidepanel/export-sidepanel.component';
import { IsOneStreamLoading } from '../../../helpers/is-one-stream-loading.helper';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { LuceneParser } from '../../../helpers/lucene-parser.helper';
import { LuceneHelper } from '../../../helpers/lucene.helper';
import { LoadSavedQueryDialogComponent } from '../../../saved-queries/components/load-saved-query-dialog/load-saved-query.component';
import { SaveQueryDialogComponent } from '../../../saved-queries/components/save-query-dialog/save-query-dialog.component';
import { UpdateQueryDialogComponent } from '../../../saved-queries/components/update-query-dialog/update-query-dialog.component';
import { SavedQueriesSandboxService } from '../../../saved-queries/sandbox/saved-queries.sandbox';
import { OverviewOptions } from '../../../state/overview/overview-options.entity';
import { ActiveFilterPill } from '../../components/active-filter-bar/entities/active-filter-pill.entity';
import {
    HierarchicalListComponent,
    HIERARCHY_TABLE_SCROLL_REF
} from '../../components/hierarchical-list/hierarchical-list.component';
import { CARD_TABLE_SCROLL_REF, ListComponent } from '../../components/list/list.component';
import { SubmapLevelSearch } from '../../components/submap-tree/submap-tree.component';
import { BatchActionItemFactory, createBatchActionItems } from '../../entities/action/batch-action-item.entity';
import { RecordActionItem } from '../../entities/action/record-action-item.class';
import { ApiFragment } from '../../entities/api-fragment.entity';
import { ColumnDef } from '../../entities/column-definition.entity';
import { Column } from '../../entities/column.entity';
import { HierarchyColumn } from '../../entities/hierarchy-column.entity';
import {
    ExpansionState,
    HierarchyRow,
    SKELETON_ROW_COUNT,
    toChildRow,
    toMessageRow,
    toRecordRow,
    toShowMoreRow,
    toSkeletonRows,
    toSubheaderRow
} from '../../entities/hierarchy-row.entity';
import { DottedKey, MetadataField } from '../../entities/metadata-field.entity';
import { RecordType } from '../../entities/metadata.entity';
import { PaginatedResults } from '../../entities/pagination/paginated-results.entity';
import { PaginationInfo } from '../../entities/pagination/pagination-info.entity';
import { RecordSearch } from '../../entities/record-search.entity';
import { StukFormats } from '../../entities/stuk-formats.entity';
import { SubmapFacetValue } from '../../entities/submap-node.entity';
import {
    ColumnEnumFilter,
    columnEnumFilterFor,
    groupFacetsByField,
    toEnumFacetFieldKeys
} from '../../helpers/column-enum-facet.helper';
import { groupBySubmap, toSubmapPath } from '../../helpers/group-by-submap.helper';
import { resetOrphanedSort } from '../../helpers/orphaned-sort-reset.helper';
import { GetChildOverviewId, GetOverviewId } from '../../helpers/overview-id.helper';
import { toPathCaption, warnIfPathFamilyMissing } from '../../helpers/path-caption.helper';
import { openRecordPreview } from '../../helpers/record-preview.helper';
import { savedQueryToStructuredQuery } from '../../helpers/saved-query-load.helper';
import { mergeSubmapValues, toLevelPrefix, toSubmapNodes } from '../../helpers/submap-tree.helper';
import { RecordSandboxService } from '../../sandbox/record-sandbox.service';
import { ActiveFilterStateService } from '../../services/active-filter-state.service';
import { facetOrderForView, FacetOrderForView, FacetOrderService } from '../../services/facet-order.service';
import { HierarchyService, ParentKind } from '../../services/hierarchy.service';
import { StukCountService } from '../../services/stuk-count.service';
import { StukFormatsService } from '../../services/stuk-formats.service';
import { SubmapFacetService } from '../../services/submap-facet.service';

/** One page entry, paired with everything the tree needs to decide before the record stream resolves. */
interface PageEntry {
    readonly id: string;
    readonly expandable: boolean;
    readonly pathCaption: string | null;
    /** The folder alone — {@link pathCaption} without the filename it ends in. What a group is keyed by. */
    readonly submap: string;
    readonly record$: Observable<ApiFragment>;
}

/**
 * Whether a query asks for less than everything. Deliberately not `isEqual(query, EmptyStructuredQuery)`:
 * `new ApiQuery([])` — which is what an empty facet selection and an empty column filter both produce — carries
 * an advanced-search group with no terms in it, so it compares unequal while narrowing nothing.
 */
function narrowsResults(structuredQuery: StructuredQuery): boolean {
    return (
        structuredQuery.Global.length > 0 ||
        !!structuredQuery.FullText ||
        !!structuredQuery.Semantic ||
        structuredQuery.Facets.length > 0 ||
        structuredQuery.Fields.length > 0 ||
        structuredQuery.ColumnFilters.length > 0 ||
        structuredQuery.AdvancedSearch.Groups.some(group => (group.Terms?.length ?? 0) > 0)
    );
}

export type TreeMode = 'dossier' | 'stuk' | 'flat';

interface TreeModeInputs {
    readonly facets: QueryPartData[];
    readonly columnFilters: QueryPartData[];
    readonly searchBar: StructuredQuery;
    readonly page: StructuredQuery;
}

function toQueryParts(structuredQuery: StructuredQuery): QueryPartData[] {
    return [
        ...FacetsToQueryParts(structuredQuery.Facets),
        ...structuredQuery.Fields,
        ...structuredQuery.ColumnFilters,
        ...structuredQuery.AdvancedSearch.Groups.flatMap(group => group.Terms ?? []).map(term => ({
            field: term.DottedKey,
            strategy: term.Operand === Operand.Exclude ? QueryPartStrategy.MatchNone : QueryPartStrategy.MatchAll,
            values: term.Values ?? []
        }))
    ];
}

/**
 * The URL params a saved query's "changed" dot cares about: `q` and `advQ` are what the query itself
 * carries, `f` (facets) isn't stored on the saved query but still counts as "on-screen search differs
 * from what's saved" from the user's point of view.
 */
interface QueryComparisonParams {
    readonly q?: string;
    readonly advQ?: string;
    readonly f?: string;
}

function toQueryComparisonParams(params: Params): QueryComparisonParams {
    return { q: params['q'], advQ: params['advQ'], f: params['f'] };
}

/** Below this, an export is fast/small enough that the "are you sure" dialog is just friction. */
const EXPORT_CONFIRM_THRESHOLD = 500;

/** One export about to be sent, and how many objects the "are you sure" threshold counts for it. */
interface ExportPlan {
    readonly scope: ExportScope;
    readonly payload: ExportStartPayload;
    readonly count: number;
}

/** What a selected-scope export names explicitly: the ticked rows, plus every ticked dossier's candidates. */
interface SelectedExport {
    readonly ticked: ApiFragment[];
    readonly candidates: ApiFragment[];
}

/**
 * `null` for an empty id list, which is not a narrower export but the whole filtered set — the backend falls
 * through to `Q`. The panel keeps that unreachable; this is the backstop. The threshold counts ticked rows,
 * as the panel does.
 */
function toSelectedPlan(selection: SelectedExport, payload: ExportStartPayload): ExportPlan | null {
    const subjectIds: string[] = [...selection.ticked, ...selection.candidates].map(record =>
        record.getMediaObjectId()
    );

    if (subjectIds.length === 0) {
        return null;
    }

    return {
        scope: { kind: 'selected', subjectIds: subjectIds as [string, ...string[]] },
        payload,
        count: selection.ticked.length
    };
}

/**
 * How long the Submap tree's per-level search box waits before it asks the engine. Each keystroke would
 * otherwise be one `POST /v2/records/facets`.
 */
const SUBMAP_SEARCH_DEBOUNCE_MS = 300;

@Component({
    standalone: false,
    selector: 'digihaven-record-overview',
    templateUrl: 'record-overview-container.component.html',
    styleUrls: ['record-overview-container.component.scss'],
    host: { '[class.dgh-overview-page]': 'standalonePage && !condensed' }
})
export class RecordOverviewContainerComponent
    extends AbstractRecordOverviewContainerComponent
    implements OnInit, OnDestroy
{
    private viewId: string;
    private readonly currentSortOptions$: BehaviorSubject<SortOption[]> = new BehaviorSubject<SortOption[]>([]);
    readonly currentColumnSelection$: BehaviorSubject<ColumnDef<ApiFragment>[]> = new BehaviorSubject<
        ColumnDef<ApiFragment>[]
    >([]);

    /** HFBD-7a: the stuk level's selection, kept apart from the dossier level's so neither disturbs the other. */
    private readonly currentChildColumnSelection$: BehaviorSubject<ColumnDef<ApiFragment>[] | null> =
        new BehaviorSubject<ColumnDef<ApiFragment>[] | null>(null);

    private readonly currentRecordActionItems$: BehaviorSubject<RecordActionItem<ApiFragment>[]> = new BehaviorSubject<
        RecordActionItem<ApiFragment>[]
    >([]);

    private readonly currentBulkActionItems$: BehaviorSubject<RecordActionItem<ApiFragment[]>[]> = new BehaviorSubject<
        RecordActionItem<ApiFragment[]>[]
    >([]);

    readonly externalQuery$: BehaviorSubject<ApiQuery | null> = new BehaviorSubject<ApiQuery | null>(null);
    private readonly enrichedRecords$: BehaviorSubject<Map<string, ApiFragment>> = new BehaviorSubject<
        Map<string, ApiFragment>
    >(new Map<string, ApiFragment>());

    readonly facetQuery$: BehaviorSubject<ApiQuery | null> = new BehaviorSubject<ApiQuery | null>(null);
    readonly columnFilterQuery$: BehaviorSubject<ApiQuery | null> = new BehaviorSubject<ApiQuery | null>(null);

    readonly recordsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    readonly fieldDefinitionsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    readonly searchBarQuery$: BehaviorSubject<ApiQuery | null> = new BehaviorSubject<ApiQuery | null>(null);
    readonly sortInfo$: BehaviorSubject<SortInfo | null> = new BehaviorSubject<SortInfo | null>(null);
    readonly refresh$: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    readonly clearSelection$: Subject<void> = new Subject<void>();
    /** HFBD-53: whether the export sidepanel overlay is open. Container owns this; the panel emits `close`. */
    readonly exportPanelOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly exportSubmitting$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly exportFailure$: BehaviorSubject<ExportFailureBanner | null> =
        new BehaviorSubject<ExportFailureBanner | null>(null);

    readonly defaultOrganisation$: ReplaySubject<Organisation> = new ReplaySubject<Organisation>(1);
    readonly backToSearchLabel$: ReplaySubject<string> = new ReplaySubject<string>(1);
    readonly batchActionItemsFactories$ = new BehaviorSubject<BatchActionItemFactory[]>([]);
    readonly globalActionItems$ = new BehaviorSubject<ActionItem[]>([]);

    userQuery$!: Observable<RecordSearch>;
    /** {@link userQuery$}'s `Q`, cleaned the same way the search bar's own "save" action cleans it. */
    currentQuery$!: Observable<string>;
    /**
     * Compares **structured** queries rather than raw Lucene text (ticket 07): re-serialising the same
     * facets/search term can format differently than what was originally saved, so a raw-string compare
     * would drift false even with nothing actually changed.
     */
    canUpdateQuery$!: Observable<boolean>;
    /** `null` once nothing is active, or while {@link savedQueries$} has not resolved yet. */
    activeQuery$!: Observable<SavedQuery | null>;
    /** Whether the search on screen has anything worth saving as a brand new saved query. */
    canSaveQuery$!: Observable<boolean>;
    records$!: Observable<PaginatedResults<ApiFragment> | null>;
    recordList$!: Observable<Observable<ApiFragment>[]>;
    /** HFBD-7a: the same page as `recordList$`, keyed by record id and interleaved with the open expansions. */
    hierarchyRows$!: Observable<HierarchyRow<ApiFragment>[]>;
    treeMode$!: Observable<TreeMode>;
    private treeModeSnapshot: TreeMode = 'flat';
    /** HFBD-85: what the grid table is *told*. {@link treeMode$} is `'flat'` on a page with no tree. */
    tableFlattened$!: Observable<boolean>;
    /** HFBD-5: see {@link initFacetResultTotal}. `undefined` means "no trustworthy total, skip the rule". */
    facetResultTotal$!: Observable<number | undefined>;
    /**
     * HFBD-75: whether the table can only be showing stuk-level records, and so renders the stuk column set
     * rather than the dossier one. Read off the **query**, never off the records that came back — results are
     * paged, and a rule that read them would let the header change between page 1 and page 2 of one result set.
     */
    isStukLevelOnly$!: Observable<boolean>;
    /** HFBD-7a: the header count, which counts dossiers rather than objects in a dossier tree. */
    resultsTitle$!: Observable<string>;
    pagedResult$!: Observable<PaginationInfo | null>;
    fieldDefinitions$!: Observable<FieldDefinition[]>;
    recordColumns$!: Observable<Column<ApiFragment>[]>;
    /**
     * HFBD-7a: the stuk level's columns, or `null` while the page configures only the dossier level — the
     * table falls back to the dossier set on `null`, where an empty array would leave child rows cell-less.
     */
    childColumns$!: Observable<Column<ApiFragment>[] | null>;
    /**
     * HFBD-75: the set the table actually renders — {@link childColumns$} while {@link isStukLevelOnly$} holds,
     * {@link recordColumns$} otherwise. Swapped **here only**: `recordColumns$` still feeds the active-filter
     * pill bar, which resolves a column filter's label by finding its column, and that is what keeps a filter
     * on a now-hidden dossier column named and removable. Two surfaces describing columns from different sets
     * is the cost, taken knowingly.
     */
    tableColumns$!: Observable<Column<ApiFragment>[]>;
    columnFilters$!: Observable<QueryPartData[]>;
    activeFacetFilters$!: Observable<QueryPartData[]>;
    enumFacetFieldKeys$!: Observable<string[]>;
    /** What the filter panel reports it is showing. `null` until it reports — see {@link initFacetFilterState}. */
    private readonly panelVisibleFacetKeys$: BehaviorSubject<readonly string[] | null> = new BehaviorSubject<
        readonly string[] | null
    >(null);
    enumFacets$!: Observable<ReadonlyMap<string, ShFacet>>;

    /** Active-filter pills shown above the table. Populated so far with facet (primary) pills only. */
    activeFilterPills$!: Observable<ActiveFilterPill[]>;

    /**
     * The saved queries (server-side, groups-shared) visible to the current user; `null` until
     * `enableSavedQueries` resolves the fetch, and again on failure — {@link savedQueriesError$} tells the
     * two apart.
     */
    savedQueries$: Observable<SavedQuery[] | null> = of(null);

    /** True once the fetch behind {@link savedQueries$} has failed. */
    savedQueriesError$: Observable<boolean> = of(false);

    /** The organisation-wide total; `savedQueries$` alone cannot give this once the fetch is capped. */
    savedQueriesTotalCount$: Observable<number> = of(0);

    /**
     * Which saved query the on-screen search was loaded from, read **from the URL** (HFBD-143).
     *
     * Deliberately derived rather than held: it used to be a component-local signal, which is why a
     * reload lost it (the search survived in `q`/`advQ`/`cf`, the name did not) and why "Alles wissen"
     * could clear the URL while the name stayed on screen — HFBD-144. One writer, one reader, no
     * second copy to fall out of step with.
     */
    private activeQueryId$!: Observable<string | null>;

    /**
     * The `q`/`advQ`/`f` URL params as they were right after the last {@link onLoadSavedQuery} call (a fresh
     * load, or the re-apply after a save) — the "changed" dot's baseline. Compared directly against the raw
     * params on screen instead of parsing either side into a `StructuredQuery`: `active.Query`'s Lucene
     * round-trip canonicalizes group shape differently than the verbatim on-screen shape, so any
     * content-based comparison at that level kept disagreeing with itself right after a save.
     *
     * HFBD-165: a subject rather than a plain field, because {@link canUpdateQuery$} reads it. As a field
     * it was only re-read when one of that stream's *other* inputs emitted, so re-applying a query whose
     * params the URL already carries (the update-in-place path) refreshed the baseline without anything
     * recomputing the dot, and `shareReplay(1)` kept handing out the stale `true`.
     */
    private readonly loadedQueryParams$: BehaviorSubject<QueryComparisonParams | null> =
        new BehaviorSubject<QueryComparisonParams | null>(null);

    /**
     * A query just created via {@link onSaveSavedQuery} lands here immediately, ahead of {@link savedQueries$}'s
     * own (already-fetched) list — otherwise `activeQuery$` can't find it by id until the next full re-fetch,
     * and the inline row would keep showing "Huidige zoekopdracht" right after saving instead of its new name.
     */
    private readonly locallySavedQueries$: BehaviorSubject<SavedQuery[]> = new BehaviorSubject<SavedQuery[]>([]);

    /** The enum-column dropdowns, bound to this page's own facet endpoint. */
    private readonly enumFilter: ColumnEnumFilter = columnEnumFilterFor(
        inject(ShFacetDataService),
        inject(ShUrlQueryService),
        this.translateService
    );

    private facetApiQuery$!: Observable<ApiQuery>;

    // #region HFBD-6 Submap filter
    /**
     * The facet the Submap tree stands in for. The tree draws from `Dynamic.DocumentPath` but the **query**
     * stays on the pre-segmented field, which is why the pill, HFBD-2 collapsing and HFBD-3 reordering all
     * come free and no tenant change is on the critical path.
     */
    protected readonly submapFacetKey: string = DottedKey.DocumentPathTagsTag;

    /** The folders of this dossier, as `Dynamic.DocumentPath` buckets. Empty on every other surface. */
    submapValues$!: Observable<SubmapFacetValue[]>;

    /** The values this facet carries in the `f` URL parameter, in whatever order it holds them. */
    submapSegments$!: Observable<string[]>;

    /**
     * Filters the panel must not render. ⚠ Starts by suppressing the Submap key, so a dossier with no folders
     * never shows the flat segment list even for a moment — the panel's own response cannot decide this, and
     * an empty tree that appears and vanishes is worse than one that never appears.
     */
    suppressedFacetKeys$!: Observable<ReadonlySet<string>>;

    /** Whether a submap value is active while the list is empty, which is what the empty state explains. */
    staleSubmapSegments$!: Observable<string[]>;

    private readonly submapSearch$: BehaviorSubject<SubmapLevelSearch | null> =
        new BehaviorSubject<SubmapLevelSearch | null>(null);

    /** The Submap tree's body, handed to the panel so the tree renders inside the facet it replaces. */
    @ViewChild('submapBody', { static: true }) private submapBody?: TemplateRef<ShFacetBodyContext>;

    protected submapBodyTemplates: ReadonlyMap<string, TemplateRef<ShFacetBodyContext>> | null = null;

    private readonly submapFacetService: SubmapFacetService = inject(SubmapFacetService);
    private readonly router: Router = inject(Router);
    // #endregion
    private latestFieldDefinitions: FieldDefinition[] = [];

    rowActionItems$!: Observable<Observable<ActionItem>[]>;
    /** HFBD-7a: the same row menus as `rowActionItems$`, keyed by `HierarchyRow.key` instead of by position. */
    hierarchyRowActions$!: Observable<ReadonlyMap<string, Observable<ActionItem>>>;
    sortOptions$!: Observable<SortOption[]>;
    currentSort$!: Observable<SortInfo>;
    /** One entry per hierarchy level this overview renders a picker for; empty when it has no picker at all. */
    /** The overviews the shared search row manages — what its Save, Reset and discard act on. */
    managedViewIds: string[] = [];

    readonly selectedRecords$: BehaviorSubject<Observable<ApiFragment>[]> = new BehaviorSubject<
        Observable<ApiFragment>[]
    >([]);

    private readonly enableRowSelect$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    selectedRecordCount$!: Observable<number>;
    actionBarActionItems$!: Observable<Observable<ActionItem | null>[]>;
    shouldEnableSelect$!: Observable<boolean>;
    overViewOptions$!: Observable<OverviewOptions | undefined>;

    /**
     * The store key everything per-view is read under, kept in step with the route. "Vorig/Volgend resultaat"
     * moves to another record on the same route config, so Angular reuses this component rather than building
     * a new one; a key resolved once in `ngOnInit` would keep reading the first record's state while every
     * write — which calls {@link getViewId} at the time — lands under the new one.
     */
    private viewId$!: Observable<string>;

    recordTypes$: Observable<RecordType[]>;

    facetProfile$!: Observable<FacetProfile>;
    private latestFacetProfile?: FacetProfile;
    facetOrder!: FacetOrderForView;
    facetQueryRestrictions$: BehaviorSubject<Restriction> = new BehaviorSubject<Restriction>(Combine.And());

    /** Null until a page sets {@link childRestrictions}; the page's own restriction stands in until it does. */
    private readonly childRestrictions$: BehaviorSubject<Restriction | null> = new BehaviorSubject<Restriction | null>(
        null
    );

    batchActionItems$: Observable<ActionItem[]>;

    readonly INCLUDE_MISSING_VALUE_FACETS: boolean = false;
    readonly SHOW_EMPTY_FACETS: boolean = false;

    // #region Hierarchy (HFBD-7a)
    /**
     * Under the dossier tree, stukken appear **only** as children, so the top level stops returning them. A flat
     * list drops this constraint (see {@link treeMode$}). Weesdocumenten belong to no dossier, so they show only
     * in a flat list.
     */
    private static readonly TOP_LEVEL_QUERY: ApiQuery = new ApiQuery([
        new QueryPart({
            field: MetadataField.RecordType,
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: [RecordType.Dossier]
        })
    ]);

    private static readonly NESTED_STUK_TOP_LEVEL_QUERY: ApiQuery = new ApiQuery([
        new QueryPart({
            field: MetadataField.ReferenceCodesDocument,
            strategy: QueryPartStrategy.MatchNone,
            values: [HierarchyService.ANY_VALUE]
        })
    ]);

    private static readonly TOP_LEVEL_QUERIES: Record<TreeMode, ApiQuery | null> = {
        dossier: RecordOverviewContainerComponent.TOP_LEVEL_QUERY,
        stuk: RecordOverviewContainerComponent.NESTED_STUK_TOP_LEVEL_QUERY,
        flat: null
    };

    private static readonly RESULTS_TITLE_KEYS: Record<TreeMode, string | null> = {
        dossier: 'hierarchy.results',
        stuk: 'hierarchy.stuk-results',
        flat: null
    };

    /** The two keys of a `Type` filter. A loaded bewaarde weergave uses `RecordType`. */
    private static readonly RECORD_TYPE_FIELDS: string[] = [MetadataField.RecordType, DottedKey.RecordType];

    /**
     * HFBD-75: the record types the stuk column set describes, and so the ones a `Type` filter can swap the
     * table's header for.
     *
     * A **closed whitelist of three**, which inverts the obvious reading of the request. The tenant's `Type`
     * vocabulary holds twenty values; one is top-level and **sixteen are neither** (Serie, Sjabloonserie, SIP,
     * Aanvraag, Classificatie, Opslaglocatie, Verpakking, …). A rule phrased as "no dossier is possible,
     * therefore show stuk columns" would put an *Opslaglocatie* under `Formaten`.
     *
     * Filtered against {@link TOP_LEVEL_QUERY} rather than simply listed, so the predicate and the query the
     * tree runs cannot drift: a type promoted to the tree's top level stops being stuk-level in the same edit.
     *
     * **A weesdocument is on the list although it is not a stuk** (added 2026-09-22). It is the one type a user
     * reaches *only* through this filter — the tree dropped it from the top level, so a `Type = Weesdocument`
     * selection is the whole of its overview — and the dossier set it used to get describes it by `Aantal
     * stukken`, an aggregate it can never have, while withholding `Formaten`, the one column that says what the
     * document actually is. Structurally it is a stuk minus its dossier: same representaties, same
     * `Representaties` tab.
     *
     * The objection that used to keep it off — that `Formaten` would resolve from its own record instead of
     * from its representaties — is answered rather than accepted: {@link ApiFragment.hasRepresentationFormats}
     * now covers it, so {@link initFlatStukFormats} batches its lookup with the stukken's and the cell reads the
     * same grouped answer.
     */
    private static readonly STUK_LEVEL_TYPES: readonly string[] = [
        RecordType.Document,
        RecordType.OrphanDocument,
        RecordType.Representation
    ].filter(
        recordType =>
            !RecordOverviewContainerComponent.TOP_LEVEL_QUERY.getSearchValues(MetadataField.RecordType).includes(
                recordType
            )
    );

    /**
     * The stuk level's "Toon submappen" preference — not a column, so it lives beside `enableHierarchy`
     * rather than in {@link tableColumns$}. `defer` reads {@link isChildColumnManaged} at subscribe time
     * (after `ngOnInit` has finished configuring column management), not at field-initialization time,
     * which runs before it.
     */
    readonly showSubfolders$: Observable<boolean> = defer(() =>
        this.isChildColumnManaged() ? this.columnManagementService.getShowSubfolders$(this.getChildViewId()) : of(true)
    );

    /**
     * The children fetched per open dossier. Deliberately not cached beyond the container's life: returning
     * from a record's detail page re-fetches, so nothing on screen can be stale.
     */
    private readonly expansions$: BehaviorSubject<ReadonlyMap<string, ExpansionState<ApiFragment>>> =
        new BehaviorSubject<ReadonlyMap<string, ExpansionState<ApiFragment>>>(new Map());

    /** Which dossiers are open. Read from the per-view store slice, which outlives this container. */
    private expandedIds$!: Observable<string[]>;
    // #endregion

    /** Only one of the two tables is ever rendered — `enableHierarchy` picks — so both are optional. */
    @ViewChild(ListComponent) recordList?: ListComponent<ApiFragment>;
    @ViewChild(HierarchicalListComponent) hierarchyList?: HierarchicalListComponent<ApiFragment>;

    // TODO: remove this emitter once navigationWithOptions is used everywhere
    @Output()
    navigation: EventEmitter<ApiFragment> = new EventEmitter<ApiFragment>();

    @Output()
    navigationWithOptions: EventEmitter<NavigationQuery<ApiFragment>> = new EventEmitter<
        NavigationQuery<ApiFragment>
    >();

    @Output()
    queryChanged: EventEmitter<ApiQuery> = new EventEmitter<ApiQuery>();

    @Output()
    sortChanged: EventEmitter<SortInfo> = new EventEmitter<SortInfo>();

    @Input()
    pageTitle: string = this.translateService.instant('all-records.title');

    @Input()
    hiddenFacetIds: string[] = [];

    /**
     * HFBD-6: whether every record this overview can list belongs to **one** dossier — both Inhoud tabs, and
     * nothing else today.
     *
     * ⚠ It is a claim about the page's **scope**, not about the records that happened to come back, and it is
     * what two rules turn on. A submap header drops the dossier segment only here (see {@link toSubmapPath}),
     * and the Submap filter renders only here — because a folder key holds no dossier in it, so on a page
     * listing many dossiers a `2024` folder from one dossier would silently merge with a `2024` from another.
     */
    @Input()
    dossierScoped = false;

    @Input()
    facetTag: string | undefined;

    @Input()
    advancedSearchTag: string | undefined;

    @Input()
    notifications: NotificationItem[] = [];

    @Input()
    set sortOptions(sortOptions: SortOption[]) {
        if (sortOptions) {
            this.currentSortOptions$.next(sortOptions);
        }
    }

    @Input() standalonePage = true;
    @Input() condensed = false;

    readonly filterPanel: FilterPanelState = filterPanelForViewport();

    get filtersHidden(): boolean {
        return !this.filterPanel.open;
    }

    onToggleFilterPanel(): void {
        this.filterPanel.toggle();
    }

    @Input()
    set columns(columns: ColumnDef<ApiFragment>[]) {
        this.currentColumnSelection$.next(columns);
    }

    @Input()
    set actionItems(actionItems: RecordActionItem<ApiFragment>[]) {
        this.currentRecordActionItems$.next(actionItems);
    }

    @Input()
    set bulkActionItems(bulkActionItems: RecordActionItem<ApiFragment[]>[]) {
        if (bulkActionItems) {
            this.currentBulkActionItems$.next(bulkActionItems);
        }
    }

    @Input()
    set batchActionItems(value: BatchActionItemFactory[]) {
        this.batchActionItemsFactories$.next(value);
    }

    @Input()
    set globalActionItems(globalActionItems: ActionItem[]) {
        this.globalActionItems$.next(globalActionItems);
    }

    @Input()
    set query(query: ApiQuery) {
        if (query) {
            this.externalQuery$.next(query);
        }
    }

    @Input()
    set restrictions(restrictions: Restriction) {
        if (restrictions) {
            this.facetQueryRestrictions$.next(restrictions);
        }
    }

    /**
     * HFBD-7a: what a request *below* the top level is restricted by, for pages where that differs.
     *
     * A page's own restriction says which records belong at its top level — `RecordType: Dossier`, a status
     * list — and every child request carries it as a *required* clause. Left to itself, a page scoped to
     * dossiers therefore asks the engine for records that are both a dossier and a stuk, and every expansion,
     * `Aantal stukken` and candidate lookup comes back empty. Such a page passes its security-only restriction
     * here; a page whose scope a stuk can satisfy needs nothing.
     */
    @Input()
    set childRestrictions(restrictions: Restriction) {
        if (restrictions) {
            this.childRestrictions$.next(restrictions);
        }
    }

    /** {@link childRestrictions} once a page has set it, the page's own restriction until then. */
    private get treeRestrictions$(): Observable<Restriction> {
        return combineLatest([this.childRestrictions$, this.facetQueryRestrictions$]).pipe(
            map(([childRestrictions, pageRestrictions]) => childRestrictions ?? pageRestrictions)
        );
    }

    private get treeRestrictions(): Restriction {
        return this.childRestrictions$.value ?? this.facetQueryRestrictions$.value;
    }

    @Input()
    titleMapperFn: (objectCount: number) => string = this.mapCountToTitle.bind(this);

    @Input() externalRefresh$?: Observable<any>;

    @Input() enableSavedQueries = false;

    @Input() enableAdvancedSearch = true;

    @Input() collapseFacetsByDefault = true;

    /**
     * HFBD-5: whether to drop filters that cannot narrow the current list. Default on and set by no call
     * site — a seam for undoing an unexpected effect in one app without a revert.
     */
    @Input() hideNonDiscriminatingFacets = true;

    /** HFBD-53: opt-in per overview. When true, the "Download weergave" button + export sidepanel render. */
    @Input() enableExportPanel = false;

    /**
     * HFBD-7a: opt-in per overview. When true, this overview renders the hierarchical grid table instead of
     * `digihaven-list`. Off everywhere but the two pilot pages, so the flat path stays exactly as it was.
     */
    @Input() enableHierarchy = false;

    /**
     * HFBD-85: opt-in per overview, for a page that wants the grid table's *flat* mode — hairline rows, header
     * sort, Kolombeheer — without a tree. Off everywhere but the three E-depot detail surfaces.
     *
     * It exists only to stage the migration off `digihaven-list`; delete it, and the flat branch with it, once
     * every `digihaven-record-overview` call site sets it — which cannot happen while the print and
     * packaging-slip overviews still pass `condensed`, since the grid table has no zebra equivalent.
     */
    @Input() enableGridTable = false;

    @Input() enableNestedStukken = false;

    /**
     * Opt-in per overview: when true, a stuk row carries a checkbox of its own. Selecting a stuk selects that
     * stuk and nothing else — ticking its dossier still says nothing about it, and the other way round.
     */
    @Input() enableStukSelect = false;

    /**
     * HFBD-86: opt-in per overview, for the export pages that have no bulk actions and so would otherwise
     * render no checkbox column at all. It only ever *adds* — a page that resolves a bulk action keeps its
     * checkboxes whatever this says.
     */
    @Input()
    set enableRowSelect(enableRowSelect: boolean) {
        this.enableRowSelect$.next(enableRowSelect);
    }

    @Output() recordsPagedResult: EventEmitter<PaginationInfo | null> = new EventEmitter<PaginationInfo | null>();

    @Input() set defaultOrganisation(organisation: Organisation) {
        if (organisation) {
            this.defaultOrganisation$.next(organisation);
        }
    }

    /**
     * HFBD-85: the id column management registers and persists under, for a surface the route cannot name.
     * Deliberately column-scoped — the route id still keys the sort, the saved views, the facet order, the
     * page cache, the overview stack and the expanded ids, all of which are *meant* to be per-route: two
     * dossiers genuinely are two places to come back from. Only the column selection's per-route identity is
     * the bug, because {@link GetOverviewId} resolves `:id` before it builds the key.
     */
    @Input() columnOverviewId?: string;

    @Input() topLevelOverview = true;

    @Input() set backToSearchLabel(backToSearchLabel: string) {
        if (backToSearchLabel) {
            this.backToSearchLabel$.next(backToSearchLabel);
        }
    }

    @Input()
    set sort(sort: SortInfo) {
        if (sort) {
            this.sortInfo$.next(sort);
        }
    }

    /**
     * HFBD-85: which of the two tables renders. Derived rather than read off one flag, because the derivation is
     * what makes **a tree without the grid table** unrepresentable in effect: `digihaven-list` has no tree
     * support at all, so that pairing is a silent total failure rather than a degraded rendering.
     *
     * `condensed` is the print/embedded zebra table, which the grid has no mode for — it can never opt in.
     */
    get rendersGridTable(): boolean {
        return !this.condensed && (this.enableHierarchy || this.enableGridTable);
    }

    get rendersTree(): boolean {
        return this.enableHierarchy || this.enableNestedStukken;
    }

    get paginationScrollRef(): string | undefined {
        if (!this.standalonePage || this.condensed) {
            return undefined;
        }

        return this.rendersGridTable ? HIERARCHY_TABLE_SCROLL_REF : CARD_TABLE_SCROLL_REF;
    }

    constructor(
        private readonly sandbox: RecordSandboxService,
        private luceneHelper: LuceneHelper,
        private readonly activatedRoute: ActivatedRoute,
        private readonly columnManagementService: ColumnManagementService,
        private readonly facetDataService: ShFacetDataService,
        private readonly activeFilterState: ActiveFilterStateService,
        private readonly facetOrderService: FacetOrderService,
        private readonly savedQueriesSandbox: SavedQueriesSandboxService,
        private readonly luceneParser: LuceneParser,
        private readonly dialogService: ShDynamicDialogService,
        private readonly hierarchyService: HierarchyService,
        private readonly stukFormatsService: StukFormatsService,
        private readonly stukCountService: StukCountService,
        private readonly quickExportSandbox: QuickExportSandboxService,
        private readonly location: Location
    ) {
        super();

        this.recordTypes$ = this.externalQuery$.pipe(
            map(query => (query ? (query.getSearchValues('RecordType') as RecordType[]) : []))
        );
    }

    ngOnInit(): void {
        this.initViewId();
        super.ngOnInit();

        this.initFacets();
        this.initFacetsListener();
        this.initColumnFilterListener();
        this.initFacetFilterState();

        this.initCombinedLoading();
        this.initOverviewOptions();
        this.initHierarchyState();
        this.initSortOptions();
        this.initRecords();
        this.initRecordList();
        this.initHierarchyExpansions();
        this.initStukCounts();
        this.initFlatStukFormats();
        this.initResultsTitle();
        this.initFacetResultTotal();

        this.initFieldDefinitions();
        this.initColumns();
        this.initSortReset();
        this.initActiveFilterPills();
        this.initColumnManagement();
        this.initFacetOrder();
        this.initSavedQueries();
        this.initPagedResult();
        this.initEnableSelect();
        this.initActionItems();
        this.initSelectedRecordCountStream();
        this.initExportFailureReset();

        this.initRefreshStreams();
        this.initPageCaching();
        this.initSaveBackToLoading();

        // After initFacetFilterState, which is where facetApiQuery$ is built.
        this.initSubmapFilter();
    }

    // #region HFBD-6 Submap filter
    /**
     * HFBD-6: the **Submap** filter, a tree of the folders this dossier's stukken sit in.
     *
     * ⚠ Only on a surface scoped to one dossier. The rule is **dossier scope, not page identity**: a folder
     * key holds no dossier in it, so a filter spanning many dossiers would merge a `2024` folder in dossier A
     * with a `2024` in dossier B. Any future surface gets this filter if, and only if, it is scoped to one
     * dossier — see {@link dossierScoped}.
     */
    private initSubmapFilter(): void {
        if (!this.dossierScoped) {
            this.submapValues$ = of([]);
            this.submapSegments$ = of([]);
            this.staleSubmapSegments$ = of([]);
            /**
             * Nothing is suppressed from *here*. Suppression exists only so the Submap tree can hide its own
             * slot, and no tree renders on this surface.
             *
             * ⚠ That is **not** the same as "no other surface carries the path field". The `EdpDocuments`
             * facet profile is shared with *Documenten om te archiveren*, which lists weesdocumenten from
             * many dossiers — and there the flat segment list has to be dropped from the profile itself, via
             * `hiddenFacetIds` on that page. A page that wants no path filter says so where it is configured;
             * it is not this stream's job to guess.
             */
            this.suppressedFacetKeys$ = of(new Set<string>());

            return;
        }

        this.submapBodyTemplates = this.submapBody
            ? new Map<string, TemplateRef<ShFacetBodyContext>>([[this.submapFacetKey, this.submapBody]])
            : null;

        // One call per query builds the whole tree: the page's query is already scoped to one dossier, so
        // every path in it comes back at once and opening a folder costs nothing.
        const values$: Observable<SubmapFacetValue[]> = combineLatest([
            this.facetApiQuery$,
            this.refresh$.pipe(startWith(undefined))
        ]).pipe(
            debounceTime(0),
            switchMap(([query]) =>
                this.submapFacetService.getSubmapValues(query).pipe(catchError(() => of([] as SubmapFacetValue[])))
            ),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        // A per-level search, sent as `Value.Constraints.Prefix`. Only reachable above the engine's value
        // ceiling, where it is the one mechanism that can reach a folder the first call did not return.
        const found$: Observable<SubmapFacetValue[]> = this.submapSearch$.pipe(
            debounceTime(SUBMAP_SEARCH_DEBOUNCE_MS),
            withLatestFrom(this.facetApiQuery$, values$),
            switchMap(([search, query, base]) => {
                const prefix: string | null =
                    search === null || search.term.length === 0
                        ? null
                        : toLevelPrefix(base, search.levelSegments, search.term);

                return prefix === null
                    ? of([] as SubmapFacetValue[])
                    : this.submapFacetService
                          .getSubmapValues(query, prefix)
                          .pipe(catchError(() => of([] as SubmapFacetValue[])));
            }),
            startWith([] as SubmapFacetValue[]),
            takeUntil(this.destroy$)
        );

        this.submapValues$ = combineLatest([values$, found$]).pipe(
            map(([base, found]) => mergeSubmapValues(base, found)),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.submapSegments$ = this.urlQueryService.getSelectedFacets().pipe(
            map(selected => selected.filter(facet => facet.key === this.submapFacetKey).map(facet => facet.value)),
            distinctUntilChanged(isEqual),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.suppressedFacetKeys$ = this.submapValues$.pipe(
            map(values =>
                // The self-hide has to read the same rule the tree draws, or the filter would be suppressed
                // while the tree has rows to show.
                toSubmapNodes(values).length === 0 ? new Set([this.submapFacetKey]) : new Set<string>()
            ),
            startWith(new Set([this.submapFacetKey])),
            distinctUntilChanged(isEqual),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        /**
         * A renamed or removed folder in a shared link empties the result set, and the tree — drawing from a
         * call scoped by that empty set — goes blank at the same moment. Everything still *works*: the pill
         * renders, carries its label and removes on click, because it comes from the `f` parameter. What is
         * missing is only the explanation, so the empty state names the active submap and points at the pill.
         */
        this.staleSubmapSegments$ = combineLatest([this.submapSegments$, this.records$]).pipe(
            map(([segments, records]) => ((records?.paginationInfo.totalNumberOfResults ?? 0) === 0 ? segments : [])),
            distinctUntilChanged(isEqual),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    /**
     * ⚠ One atomic replacement of every value this facet holds, never an add plus a remove. A tree selection
     * **is** the ancestor chain, so a two-step update would leave the query naming a folder without its
     * parents in between.
     */
    onSubmapSelectionChanged(segments: readonly string[]): void {
        this.urlQueryService.replaceFacetValues(
            { key: this.submapFacetKey } as ShFacet,
            segments.map(segment => ({
                facetLabel: this.submapFacetKey,
                key: this.submapFacetKey,
                value: segment,
                valueLabel: segment,
                selectionType: FacetSelectionType.MUST
            }))
        );
    }

    onSubmapSearchChanged(search: SubmapLevelSearch): void {
        this.submapSearch$.next(search);
    }
    // #endregion

    openExportPanel(): void {
        this.filterPanel.show();
        this.exportPanelOpen$.next(true);
    }

    closeExportPanel(): void {
        this.exportPanelOpen$.next(false);
    }

    /** HFBD-10: opens the stuk's access representation in the existing full-page viewer, in a new tab. */
    openPreview(recordId: string): void {
        openRecordPreview(this.location, recordId);
    }

    /** `null` when the stuk's representaties are known but none of them is the browser-viewable access copy. */
    private toPreviewActionItem(formats: StukFormats): ActionItem | null {
        const previewRecordId: string | null = formats.previewRecordId;

        if (!previewRecordId) {
            return null;
        }

        return {
            label: this.translateService.instant('hierarchy.preview'),
            eventListener: () => this.openPreview(previewRecordId)
        };
    }

    /**
     * Assembles what goes out, then asks whatever has to be asked before it does — the analog check (HFBD-171)
     * first, the "are you sure" threshold second, so the latter counts what is actually left to send.
     * "Start download" stays disabled for the whole run, dialogs included.
     */
    onStartDownload(payload: ExportStartPayload): void {
        this.exportSubmitting$.next(true);
        this.exportFailure$.next(null);
        this.toExportPlan(payload)
            .pipe(
                catchError(err => {
                    this.quickExportSandbox.notifyFailed();
                    return throwError(() => err);
                }),
                switchMap(plan => this.confirmLargeExport(plan)),
                switchMap(plan => this.quickExportSandbox.trigger(plan.scope, plan.payload)),
                finalize(() => this.exportSubmitting$.next(false)),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: () => this.closeExportPanel(),
                error: err => this.exportFailure$.next(toFailureBanner(classifyCreateFailure(err)))
            });
    }

    /** Completes without a value when there turns out to be nothing to send, or the user stopped it. */
    private toExportPlan(payload: ExportStartPayload): Observable<ExportPlan> {
        if (payload.scope === 'filtered') {
            return this.overViewOptions$.pipe(
                take(1),
                switchMap(options =>
                    this.records$.pipe(
                        take(1),
                        map(
                            (records): ExportPlan => ({
                                scope: { kind: 'filtered', q: options?.recordSearch?.Q },
                                payload,
                                count: records?.paginationInfo?.totalNumberOfResults ?? 0
                            })
                        )
                    )
                )
            );
        }

        return this.selectedExport().pipe(switchMap(selection => this.withAnalogCheck(selection, payload)));
    }

    private confirmLargeExport(plan: ExportPlan): Observable<ExportPlan> {
        if (plan.count < EXPORT_CONFIRM_THRESHOLD) {
            return of(plan);
        }

        return this.dialogService
            .open<boolean>(ShBooleanDialogComponent, {
                title: this.translateService.instant('exports.sidepanel.confirm-dialog.title'),
                data: {
                    yes: this.translateService.instant('exports.sidepanel.confirm-dialog.yes'),
                    no: this.translateService.instant('exports.sidepanel.confirm-dialog.no'),
                    message: this.translateService.instant('exports.sidepanel.confirm-dialog.message', {
                        amount: plan.count
                    })
                }
            })
            .afterClosed$.pipe(
                take(1),
                filter(confirmed => confirmed),
                map(() => plan)
            );
    }

    private initExportFailureReset(): void {
        combineLatest([
            this.records$.pipe(map(records => records?.paginationInfo?.totalNumberOfResults ?? 0)),
            this.selectedRecordCount$
        ])
            .pipe(distinctUntilChanged(isEqual), skip(1), takeUntil(this.destroy$))
            .subscribe(() => this.exportFailure$.next(null));
    }

    onExportScopeChanged(): void {
        this.exportFailure$.next(null);
    }

    /**
     * A ticked dossier costs one id — the engine expands its permanent subtree. Only its *candidate*
     * children, which that expansion skips, are resolved here, batched across every ticked dossier.
     */
    private selectedExport(): Observable<SelectedExport> {
        return this.selectedRecords$.pipe(
            take(1),
            switchMap((records$: Observable<ApiFragment>[]) =>
                records$.length === 0 ? of([] as ApiFragment[]) : combineLatest(records$).pipe(take(1))
            ),
            switchMap((ticked: ApiFragment[]) => {
                // Two different fields on purpose: the candidate relation stores a fragment id that only
                // the record id prefixes, while the export names records by media object id.
                const dossierIds: string[] = ticked
                    .filter(record => record.getRecordType() === RecordType.Dossier)
                    .map(record => record.getId());

                return this.stukCountService
                    .candidateChildren(dossierIds, this.treeRestrictions)
                    .pipe(map((candidates): SelectedExport => ({ ticked, candidates })));
            })
        );
    }

    /**
     * HFBD-171: a files-only export fails on any analog stuk it names explicitly — ticked, or a ticked dossier's
     * candidate — even one with a digital representatie: the analog original is an entry of its own, with no file.
     * Dossiers never do: the engine skips such stukken in its own expansion.
     */
    private withAnalogCheck(selection: SelectedExport, payload: ExportStartPayload): Observable<ExportPlan> {
        const plan: ExportPlan | null = toSelectedPlan(selection, payload);

        if (!plan) {
            return EMPTY;
        }

        if (toQuickExportContentMode(payload) !== 'FILES_ONLY') {
            return of(plan);
        }

        const isAnalog = (record: ApiFragment): boolean => record.hasRepresentationFormats() && record.isAnalogDocument();
        const unexportable: ReadonlySet<string> = new Set(
            [...selection.ticked, ...selection.candidates].filter(isAnalog).map(record => record.getId())
        );

        if (unexportable.size === 0) {
            return of(plan);
        }

        const exportable = (record: ApiFragment): boolean => !unexportable.has(record.getId());
        const reduced: ExportPlan | null = toSelectedPlan(
            {
                ticked: selection.ticked.filter(exportable),
                candidates: selection.candidates.filter(exportable)
            },
            payload
        );

        return this.askAboutAnalogStukken({ amount: unexportable.size, canRemove: !!reduced }).pipe(
            switchMap((choice): Observable<ExportPlan> => {
                if (choice === 'filesAndMetadata') {
                    return of({ ...plan, payload: { ...payload, includeFiles: true, includeMetadata: true } });
                }

                return choice === 'remove' && reduced ? of(reduced) : EMPTY;
            })
        );
    }

    private askAboutAnalogStukken(data: AnalogExportDialogData): Observable<AnalogExportChoice> {
        return this.dialogService
            .open<AnalogExportChoice>(AnalogExportDialogComponent, {
                title: this.translateService.instant('exports.sidepanel.analog-dialog.title'),
                data,
                // Wide in both variants: the choice hints wrap awkwardly in the normal width, and without the
                // remove choice the button carries the long "Exporteren als bestanden en metadata" label.
                options: { style: DialogStyle.Wide }
            })
            .afterClosed$.pipe(
                take(1),
                map(choice => choice ?? 'cancel')
            );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.refresh$.complete();
        this.exportPanelOpen$.complete();
        this.exportSubmitting$.complete();
        this.exportFailure$.complete();
        this.currentColumnSelection$.complete();
        this.currentChildColumnSelection$.complete();
        this.externalQuery$.complete();
        this.columnFilterQuery$.complete();
        this.currentSortOptions$.complete();
        this.sortInfo$.complete();
        this.recordsLoading$.complete();
        this.fieldDefinitionsLoading$.complete();
        this.searchBarQuery$.complete();
        this.clearSelection$.complete();
        this.expansions$.complete();
        this.childRestrictions$.complete();
        this.defaultOrganisation$.complete();
        this.backToSearchLabel$.complete();
        this.batchActionItemsFactories$.complete();
        this.globalActionItems$.complete();
    }

    onRecordInteraction(record: ApiFragment): void {
        // The fetch only feeds bulk-action gating; a select-all on a page with none would fire one
        // request per row for nothing.
        if (this.currentBulkActionItems$.value.length === 0) {
            return;
        }

        this.enrichedRecords$
            .pipe(
                take(1),
                filter(enrichedRecords => !enrichedRecords.get(record.getId())),
                tap(enrichedRecords => {
                    enrichedRecords.set(record.getId(), record);
                    this.enrichedRecords$.next(enrichedRecords);
                }),
                switchMap(() => this.sandbox.getById(record.getId())),
                takeUntil(this.destroy$)
            )
            .subscribe(enrichedRecord => {
                this.enrichedRecords$.value.set(record.getId(), enrichedRecord);
                this.enrichedRecords$.next(this.enrichedRecords$.value);
            });
    }

    private refreshStaleEnrichments(records: ApiFragment[]): void {
        const enriched: Map<string, ApiFragment> = this.enrichedRecords$.value;
        const stale: ApiFragment[] = records.filter(record => {
            const cached: ApiFragment | undefined = enriched.get(record.getId());
            return (
                !!cached &&
                cached.getField(DottedKey.LastModifiedDate) !== record.getField(DottedKey.LastModifiedDate)
            );
        });

        if (stale.length === 0) {
            return;
        }

        stale.forEach(record => enriched.delete(record.getId()));
        this.enrichedRecords$.next(enriched);
        stale.forEach(record => this.onRecordInteraction(record));
    }

    refreshList(): void {
        this.refresh$.next();
    }

    onQueryChange(query: ApiQuery): void {
        if (!isEqual(query, this.searchBarQuery$.value)) {
            this.searchBarQuery$.next(query);
        }
    }

    onFacetQueryChange(query: ApiQuery): void {
        if (!isEqual(query, this.facetQuery$.value)) {
            this.facetQuery$.next(query);
        }
    }

    onColumnFilterQueryChange(query: ApiQuery): void {
        if (!isEqual(query, this.columnFilterQuery$.value)) {
            this.columnFilterQuery$.next(query);
        }
    }

    onColumnFilterChanged(field: string, value: string): void {
        this.activeFilterState.setColumnFilter(field, value);
    }

    /** Lazily loads the live facet buckets for one field the first time (and each time) its column dropdown opens. */
    onEnumFilterOpened(dottedKey: string): void {
        this.facetApiQuery$.pipe(take(1), takeUntil(this.destroy$)).subscribe(query =>
            this.enumFilter.load(dottedKey, {
                query,
                facetProfile: this.latestFacetProfile,
                fieldDefinitions: this.latestFieldDefinitions
            })
        );
    }

    onEnumFilterCleared(dottedKey: string): void {
        this.enumFilter.clear(dottedKey);
    }

    onFacetValueSelected(facetValue: ShSelectedFacetValue): void {
        this.enumFilter.toggleValue(facetValue);
    }

    /** Removes one active-filter pill: facet-origin clears its facet value; column-origin clears that column's filter. */
    onRemoveActiveFilter(pill: ActiveFilterPill): void {
        this.activeFilterState.removePill(pill);
    }

    /**
     * Clears everything the bar stands for, in one navigation: the facet (`f`) and column (`cf`) state, the
     * search term, and the saved query the whole lot may have come from.
     *
     * Goes through the same reset as letting go and deleting rather than `ActiveFilterStateService.clearAll`,
     * which is what ticket 006 always said this button did and what HFBD-144 reported it did not. Note this
     * clears `q`/`advQ` too, which the old path left standing — a saved query's content *is* those params, so
     * releasing it while leaving them behind is the orphaned-terms state the delete path already avoids.
     *
     * The other surfaces that show this bar (the task and evaluate overviews) have no saved queries and keep
     * calling `ActiveFilterStateService.clearAll` unchanged.
     */
    onClearAllFilters(): void {
        this.resetActiveQuery();
    }

    onSortInfoChange(sortInfo: SortInfo): void {
        this.sortInfo$.next(sortInfo);
        this.sandbox.setSort(this.getViewId(), sortInfo);
    }

    onRowNavigation(record: ApiFragment): void {
        this.navigation.emit(record);

        this.records$
            .pipe(
                map(page => this.calculateIndexInPage(record, page)),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(indexInPage => {
                this.navigationWithOptions.emit({
                    record,
                    navigationOptions: {
                        ...defaultNavigationOptions,
                        state: {
                            view: this.getViewId(),
                            indexInPage
                        }
                    }
                });
            });
    }

    private calculateIndexInPage(record: ApiFragment, page: PaginatedResults<ApiFragment> | null): number | undefined {
        if (page == null) {
            return undefined;
        }

        return page.results.indexOf(record);
    }

    onSelectionChange(apiFragments: Observable<ApiFragment>[]): void {
        this.selectedRecords$.next(apiFragments);
    }

    /** The single way in to whichever table is rendered: both clear and select their whole page through it. */
    selectAll(selectAll: boolean): void {
        this.recordList?.toggleSelectAll(selectAll);
        this.hierarchyList?.toggleSelectAll(selectAll);
    }

    private clearBatchSelection(): void {
        this.recordList?.clearBatchSelection();
        this.hierarchyList?.clearBatchSelection();
    }

    private initRefreshStreams(): void {
        this.externalRefresh$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshList());
    }

    private initPageCaching(): void {
        this.records$
            .pipe(takeUntil(this.destroy$))
            .subscribe(records =>
                this.sandbox.initializePageCache(this.getViewId(), records?.paginationInfo.page || 0, records)
            );
    }

    private initSaveBackToLoading(): void {
        this.backToSearchLabel$
            .pipe(takeUntil(this.destroy$))
            .subscribe(backToSearchLabel => this.sandbox.setBackToSearchLabel(this.getViewId(), backToSearchLabel));
    }

    /**
     * Both list variants read the same enriched record streams; only their shape differs. The flat list takes
     * the bare observables, the hierarchical one takes rows keyed by record id — which is why the page is
     * mapped once and shared, rather than enriched twice.
     */
    private initRecordList(): void {
        const page$: Observable<PageEntry[]> = this.records$.pipe(
            filter(records => !!records),
            map(recordPage => recordPage?.results || []),
            tap(records => this.refreshStaleEnrichments(records)),
            // Only the v2 table shows a path caption, so only it has the failure worth reporting — which after
            // HFBD-85 includes the flat detail pages, hence the derived getter rather than the tree flag.
            tap(records => {
                if (this.rendersGridTable) {
                    warnIfPathFamilyMissing(records);
                }
            }),
            map(records =>
                records.map(record => ({
                    id: record.getId(),
                    // Read off the raw row rather than the enriched stream: the chevron cannot wait for it.
                    expandable: record.hasChildRecords(),
                    pathCaption: toPathCaption(record),
                    submap: toSubmapPath(record, { dropDossierSegment: this.dossierScoped }),
                    record$: this.enrichRecord(record)
                }))
            ),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.recordList$ = page$.pipe(
            map(page => page.map(entry => entry.record$)),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
        this.hierarchyRows$ = combineLatest([
            page$,
            this.expandedIds$,
            this.expansions$,
            this.tableFlattened$,
            this.showSubfolders$
        ]).pipe(
            map(([page, expandedIds, expansions, flattened, showSubfolders]) =>
                this.toHierarchyRows(page, flattened ? [] : expandedIds, expansions, flattened, showSubfolders)
            ),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    // #region Hierarchy (HFBD-7a)

    /**
     * The streams the tree needs before {@link initRecords} runs: the top-level query and the row assembly
     * both read them. The expansions themselves are started later — see {@link initHierarchyExpansions} —
     * because fetching children needs the sort, which only exists once the records stream is built.
     */
    private initHierarchyState(): void {
        const inputs$: Observable<TreeModeInputs> = combineLatest([
            this.activeFacetFilters$,
            this.columnFilters$,
            this.searchBarQuery$,
            this.externalQuery$
        ]).pipe(
            map(([facets, columnFilters, searchBarQuery, externalQuery]) => ({
                facets,
                columnFilters,
                searchBar: searchBarQuery?.structuredQuery ?? EmptyStructuredQuery,
                page: externalQuery?.structuredQuery ?? EmptyStructuredQuery
            })),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.treeMode$ = (
            this.rendersTree ? inputs$.pipe(map(inputs => this.toTreeMode(inputs))) : of('flat' as const)
        ).pipe(
            distinctUntilChanged(),
            tap(mode => (this.treeModeSnapshot = mode)),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.tableFlattened$ = this.treeMode$.pipe(map(mode => mode === 'flat'));

        this.isStukLevelOnly$ = inputs$.pipe(
            map(inputs => this.isStukLevelOnly(inputs)),
            distinctUntilChanged(),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.expandedIds$ = this.rendersTree
            ? this.overViewOptions$.pipe(
                  map(options => options?.expandedIds ?? []),
                  distinctUntilChanged(isEqual),
                  shareReplay(1),
                  takeUntil(this.destroy$)
              )
            : of([]);
    }

    private toTreeMode(inputs: TreeModeInputs): TreeMode {
        if (this.enableNestedStukken) {
            return this.narrowsApartFrom(inputs, [this.submapFacetKey]) ? 'flat' : 'stuk';
        }

        const typeValues: readonly string[] = this.readTypeValues(inputs);

        if (typeValues.length !== 1) {
            return 'flat';
        }

        if (typeValues[0] === RecordType.Dossier) {
            return 'dossier';
        }

        if (typeValues[0] !== RecordType.Document) {
            return 'flat';
        }

        return this.narrowsApartFrom(inputs, RecordOverviewContainerComponent.RECORD_TYPE_FIELDS) ? 'flat' : 'stuk';
    }

    private narrowsApartFrom(inputs: TreeModeInputs, fields: readonly string[]): boolean {
        const counts = (field: string): boolean => !fields.includes(field);
        const searchBar: StructuredQuery = inputs.searchBar;

        return (
            [...inputs.facets, ...inputs.columnFilters].some(part => counts(part.field) && part.values.length > 0) ||
            narrowsResults({
                ...searchBar,
                Facets: searchBar.Facets.filter(facet => counts(facet.DottedKey)),
                Fields: searchBar.Fields.filter(part => counts(part.field)),
                ColumnFilters: searchBar.ColumnFilters.filter(part => counts(part.field)),
                AdvancedSearch: {
                    ...searchBar.AdvancedSearch,
                    Groups: searchBar.AdvancedSearch.Groups.map(group => ({
                        ...group,
                        Terms: group.Terms?.filter(term => counts(term.DottedKey))
                    }))
                }
            })
        );
    }

    /**
     * HFBD-75: whether every record the table could be showing is one the stuk column set describes: every
     * Type value is in {@link STUK_LEVEL_TYPES}. No Type, *Dossier*, a mix, an exclusion or a type that is
     * neither (a *Serie*, a *SIP*) keeps the dossier header.
     */
    private isStukLevelOnly(inputs: TreeModeInputs): boolean {
        const typeValues: readonly string[] = this.readTypeValues(inputs);

        return (
            typeValues.length > 0 &&
            typeValues.every(value => RecordOverviewContainerComponent.STUK_LEVEL_TYPES.includes(value))
        );
    }

    private readTypeValues(inputs: TreeModeInputs): readonly string[] {
        const userTypeParts: QueryPartData[] = this.userTypeParts(inputs);
        const typeParts: QueryPartData[] =
            userTypeParts.length > 0 ? userTypeParts : this.typePartsOf(toQueryParts(inputs.page));

        if (typeParts.some(part => part.strategy === QueryPartStrategy.MatchNone)) {
            return [];
        }

        return [...new Set(typeParts.flatMap(part => part.values))];
    }

    private userTypeParts(inputs: TreeModeInputs): QueryPartData[] {
        return this.typePartsOf([...inputs.facets, ...inputs.columnFilters, ...toQueryParts(inputs.searchBar)]);
    }

    private typePartsOf(queryParts: QueryPartData[]): QueryPartData[] {
        return queryParts.filter(queryPart =>
            RecordOverviewContainerComponent.RECORD_TYPE_FIELDS.includes(queryPart.field)
        );
    }

    /**
     * Opening a dossier is what triggers its fetch; closing one throws the children away rather than caching.
     *
     * Gated on the page having arrived, for two reasons: children belong to rows that are on it, and the
     * restrictions the child query has to carry are fed in asynchronously by the page — a restored tree syncs
     * during `ngOnInit`, before either has landed. A flat list syncs to nothing, so it discards the expansions
     * rather than fetching children whose rows would never render.
     */
    private initHierarchyExpansions(): void {
        if (!this.rendersTree) {
            return;
        }

        combineLatest([this.expandedIds$, this.tableFlattened$, this.records$.pipe(filter(records => !!records))])
            .pipe(
                map(([expandedIds, flattened, records]) => ({
                    expandedIds: flattened ? [] : expandedIds,
                    pageIds: (records?.results ?? []).map(record => record.getId())
                })),
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$)
            )
            .subscribe(({ expandedIds, pageIds }) => this.syncExpansions(expandedIds, pageIds));
    }

    /**
     * `Aantal stukken` is asked for a whole page at a time, from the page itself rather than from the rows:
     * subscribing here means the counts are on their way before the cells that read them exist, so they open
     * on a skeleton instead of flashing empty.
     *
     * Only dossiers are asked about. A weesdocument or — in flat mode — a stuk has no stukken of its own, and
     * a cell with no answer renders nothing rather than a misleading `0`.
     *
     * The {@link treeRestrictions$} ride along, the same ones {@link loadChildren} carries: a count drawn from
     * a different scope than the expansion below it would promise rows that expansion then refuses to show.
     * They are fed in asynchronously by the page and can land after its first result, which is why they are
     * combined rather than sampled — the service recounts when they change, and ignores a repeat.
     */
    private initStukCounts(): void {
        if (!this.enableHierarchy) {
            return;
        }

        const dossierIds$: Observable<string[]> = this.records$.pipe(
            filter(records => !!records),
            map(records =>
                (records?.results ?? []).filter(record => record.isDossier()).map(dossier => dossier.getId())
            )
        );

        combineLatest([dossierIds$, this.treeRestrictions$])
            .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroy$))
            .subscribe(([dossierIds, restrictions]) => this.stukCountService.load(dossierIds, restrictions));
    }

    onExpandToggled(row: HierarchyRow<ApiFragment>): void {
        this.expandedIds$.pipe(take(1), takeUntil(this.destroy$)).subscribe(expandedIds => {
            const collapsing: boolean = expandedIds.includes(row.key);
            const toggled: string[] = collapsing ? expandedIds.filter(id => id !== row.key) : [...expandedIds, row.key];

            this.sandbox.setExpandedIds(this.getViewId(), toggled);
        });
    }

    onCollapseAll(): void {
        this.expandedIds$
            .pipe(
                take(1),
                filter(expandedIds => expandedIds.length > 0),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.sandbox.setExpandedIds(this.getViewId(), []));
    }

    /** Both "Toon meer stukken" and a retry after a failure resume from wherever the expansion got to. */
    onFetchMoreChildren(parentId: string): void {
        this.loadChildren(parentId, this.expansions$.value.get(parentId)?.loadedCount ?? 0);
    }

    private parentKindFor(parentId: string): ParentKind {
        return this.isStukTree || this.findChildRow(parentId) ? 'stuk' : 'dossier';
    }

    private get maxChildLevel(): number {
        return this.isStukTree ? HierarchyService.MAX_CHILD_LEVEL - 1 : HierarchyService.MAX_CHILD_LEVEL;
    }

    private get isStukTree(): boolean {
        return this.enableNestedStukken || this.treeModeSnapshot === 'stuk';
    }

    private findChildRow(key: string): HierarchyRow<ApiFragment> | undefined {
        return [...this.expansions$.value.values()]
            .flatMap(state => state.childRows)
            .find(childRow => childRow.key === key);
    }

    private syncExpansions(expandedIds: string[], pageIds: string[]): void {
        const current: ReadonlyMap<string, ExpansionState<ApiFragment>> = this.expansions$.value;
        const retained: Map<string, ExpansionState<ApiFragment>> = new Map(
            expandedIds.filter(id => current.has(id)).map(id => [id, current.get(id)!])
        );

        this.expansions$.next(retained);
        expandedIds
            .filter(id => !current.has(id) && this.isPlaced(id, pageIds))
            .forEach(id => this.loadChildren(id, 0));
    }

    private isPlaced(id: string, pageIds: string[]): boolean {
        return pageIds.includes(id) || !!this.findChildRow(id);
    }

    private loadDeeperExpansions(revealed: ApiFragment[]): void {
        this.expandedIds$.pipe(take(1), takeUntil(this.destroy$)).subscribe(expandedIds =>
            revealed
                .map(child => child.getId())
                .filter(childId => expandedIds.includes(childId) && !this.expansions$.value.has(childId))
                .forEach(childId => this.loadChildren(childId, 0))
        );
    }

    private loadChildren(parentId: string, startIndex: number): void {
        const parentKind: ParentKind = this.parentKindFor(parentId);

        this.setExpansionStatus(parentId, 'loading');

        combineLatest([this.treeRestrictions$, this.currentSort$])
            .pipe(
                take(1),
                switchMap(([restrictions, sort]) =>
                    this.hierarchyService.childrenFor(parentId, { startIndex, sort, restrictions, parentKind })
                ),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: children => this.onChildrenLoaded(parentId, startIndex, children),
                error: () => this.setExpansionStatus(parentId, 'error')
            });
    }

    private onChildrenLoaded(
        parentId: string,
        startIndex: number,
        children: PaginatedResults<ApiFragment> | null
    ): void {
        // A retry re-fetches the batch it failed on, so only a follow-on batch keeps what is already there.
        const loaded: HierarchyRow<ApiFragment>[] =
            startIndex === 0 ? [] : (this.expansions$.value.get(parentId)?.childRows ?? []);

        const revealed: ApiFragment[] = children?.results ?? [];
        this.refreshStaleEnrichments(revealed);

        // Before the rows land, so the cells they create find a state to render rather than defaulting to one.
        this.stukFormatsService.load(revealed.map(child => child.getId()));

        const level: number = this.childLevelFor(parentId);

        const childRows: HierarchyRow<ApiFragment>[] = [
            ...loaded,
            ...revealed.map(child =>
                toChildRow(
                    child.getId(),
                    parentId,
                    this.enrichRecord(child),
                    toPathCaption(child),
                    toSubmapPath(child, { dropDossierSegment: this.dossierScoped }),
                    this.enableStukSelect,
                    {
                        level,
                        expandable: level < this.maxChildLevel && child.hasChildRecords(),
                        parentCaption: child.getParentStukTitle()
                    }
                )
            )
        ];

        this.setExpansion(parentId, {
            status: childRows.length === 0 ? 'empty' : 'ready',
            childRows,
            loadedCount: childRows.length,
            totalCount: children?.paginationInfo.totalNumberOfResults ?? null
        });

        this.loadDeeperExpansions(revealed);
    }

    private childLevelFor(parentId: string): number {
        return (this.findChildRow(parentId)?.level ?? 0) + 1;
    }

    private setExpansionStatus(parentId: string, status: ExpansionState<ApiFragment>['status']): void {
        const previous: ExpansionState<ApiFragment> | undefined = this.expansions$.value.get(parentId);

        this.setExpansion(parentId, {
            status,
            childRows: previous?.childRows ?? [],
            loadedCount: previous?.loadedCount ?? 0,
            totalCount: previous?.totalCount ?? null
        });
    }

    private setExpansion(parentId: string, state: ExpansionState<ApiFragment>): void {
        const updated: Map<string, ExpansionState<ApiFragment>> = new Map(this.expansions$.value);
        updated.set(parentId, state);
        this.expansions$.next(updated);
    }

    private toHierarchyRows(
        page: PageEntry[],
        expandedIds: string[],
        expansions: ReadonlyMap<string, ExpansionState<ApiFragment>>,
        flattened: boolean,
        showSubfolders: boolean
    ): HierarchyRow<ApiFragment>[] {
        const expanded: Set<string> = new Set(expandedIds);

        const topRows: HierarchyRow<ApiFragment>[] = page.map(entry =>
            toRecordRow(entry.id, entry.record$, true, {
                expandable: !flattened && entry.expandable,
                expanded: expanded.has(entry.id),
                // Kept in flat mode as well: which records carry a path is not a question about the table's mode.
                pathCaption: entry.pathCaption,
                submap: entry.submap
            })
        );

        // HFBD-85: in the display stream, never into state — the same rule HFBD-74 set for an expansion's rows.
        return groupBySubmap(topRows, { grouped: this.groupsTopLevelBySubmap }).flatMap(row =>
            row.kind === 'record' && expanded.has(row.key)
                ? [row, ...this.toExpansionRows(row.key, row.level, expanded, expansions, showSubfolders)]
                : [row]
        );
    }

    /**
     * HFBD-85: whether the top-level rows are headed by their folder. The predicate is **natively flat** — the
     * grid table rendering with no tree — and deliberately not "the rows have a submap": the group key is the
     * bare folder path with no dossier in it, so grouping a tree page's *flattened* mode would silently merge a
     * `bestek` folder from one dossier with a `bestek` from another.
     *
     * No per-surface configuration is needed beyond that. It self-neutralises on representaties, which carry no
     * path — the tenant models one at exactly two levels, and neither is a representation.
     *
     * ⚠ Unlike an expansion, a page cannot wait to be fully loaded, so a folder straddling the page boundary
     * **reprints its header** at the top of the next page. Accepted: every stuk still sits under a correct
     * folder name, and only the claim "this is where the group starts" is wrong. Grouping only when the whole
     * result set fits on one page never lies, but silently changes the treatment's shape on a large dossier —
     * two visualisations of the same thing is worse than a repeat.
     */
    private get groupsTopLevelBySubmap(): boolean {
        return this.rendersGridTable && !this.enableHierarchy;
    }

    /**
     * The rows an open dossier contributes below itself, introduced by the stuk-level sub-header — except
     * where there is nothing for it to label: an empty dossier and a first batch that failed outright carry
     * their inline message alone.
     */
    private toExpansionRows(
        parentId: string,
        parentLevel: number,
        expanded: Set<string>,
        expansions: ReadonlyMap<string, ExpansionState<ApiFragment>>,
        showSubfolders: boolean
    ): HierarchyRow<ApiFragment>[] {
        const level: number = parentLevel + 1;
        const rows: HierarchyRow<ApiFragment>[] = this.toExpansionBodyRows(
            parentId,
            level,
            expanded,
            expansions,
            showSubfolders
        );
        const hasStukRows: boolean = rows.some(row => row.kind === 'record' || row.kind === 'skeleton');

        return hasStukRows ? [toSubheaderRow<ApiFragment>(parentId, level), ...rows] : rows;
    }

    private toNestedRows(
        row: HierarchyRow<ApiFragment>,
        expanded: Set<string>,
        expansions: ReadonlyMap<string, ExpansionState<ApiFragment>>,
        showSubfolders: boolean
    ): HierarchyRow<ApiFragment>[] {
        if (row.kind !== 'record' || !row.expandable) {
            return [row];
        }

        const isExpanded: boolean = expanded.has(row.key);
        const opened: HierarchyRow<ApiFragment> = { ...row, expanded: isExpanded };

        return isExpanded
            ? [opened, ...this.toExpansionRows(row.key, row.level, expanded, expansions, showSubfolders)]
            : [opened];
    }

    /**
     * Where the submap grouping runs: on the way to the screen, not on the way into `expansions$`. The rows it
     * inserts are display rows, and `childRows` is the count-bearing list *Toon meer* pages from — see
     * {@link ExpansionState}.
     *
     * Grouping is also gated on the very expression that decides there is no *Toon meer* row to draw, so a
     * group can never straddle that boundary: it is only ever drawn once every one of its members is present.
     */
    private toExpansionBodyRows(
        parentId: string,
        level: number,
        expanded: Set<string>,
        expansions: ReadonlyMap<string, ExpansionState<ApiFragment>>,
        showSubfolders: boolean
    ): HierarchyRow<ApiFragment>[] {
        const state: ExpansionState<ApiFragment> | undefined = expansions.get(parentId);

        if (!state) {
            return toSkeletonRows(parentId, SKELETON_ROW_COUNT, level);
        }

        const nested = (rows: HierarchyRow<ApiFragment>[]): HierarchyRow<ApiFragment>[] =>
            rows.flatMap(row => this.toNestedRows(row, expanded, expansions, showSubfolders));

        switch (state.status) {
            case 'loading':
                // A first batch gets the full set of skeletons; a follow-on batch only needs one.
                return state.childRows.length === 0
                    ? toSkeletonRows(parentId, SKELETON_ROW_COUNT, level)
                    : [...nested(state.childRows), ...toSkeletonRows<ApiFragment>(parentId, 1, level)];
            case 'empty':
                return [toMessageRow(parentId, this.expansionMessageKey('empty', level), false, { level })];
            case 'error':
                return [
                    ...nested(state.childRows),
                    toMessageRow<ApiFragment>(parentId, this.expansionMessageKey('error', level), true, { level })
                ];
            case 'ready': {
                // An unknown total is not the same as none left: it fails safe to "more may be coming", which
                // leaves the expansion on its per-row captions rather than heading a folder it cannot vouch for.
                const remaining: number | null =
                    state.totalCount === null ? null : state.totalCount - state.loadedCount;
                // "Toon submappen" (Kolombeheer, stuk level): unticking it renders these rows unheaded, same
                // as a dossier whose stukken carry no folder at all.
                const rows: HierarchyRow<ApiFragment>[] = nested(
                    groupBySubmap(state.childRows, {
                        grouped: showSubfolders && remaining !== null && remaining <= 0
                    })
                );

                return remaining !== null && remaining > 0
                    ? [...rows, toShowMoreRow(parentId, remaining, level)]
                    : rows;
            }
        }
    }

    private expansionMessageKey(state: 'empty' | 'error', level: number): string {
        return level > 1 ? `hierarchy.${state}-stuk` : `hierarchy.${state}`;
    }

    /**
     * Any change to the result set discards the tree — the dossiers that were open may not even be on the
     * page afterwards. `skip(1)` is what lets an expansion survive a trip to a record's detail page: the
     * container is rebuilt on the way back, and its first emission is the restored state, not a change.
     *
     * `debounceTime(0)` is what makes that first emission a single one. Startup arrives in pieces — facet and
     * column-filter state resolve out of the URL after the merged query has already produced a value — and
     * without it the second piece would read as a user's filter change and collapse the tree just restored.
     */
    private initExpansionReset(combinedQuery$: Observable<ApiQuery>, sort$: Observable<SortInfo | null>): void {
        if (!this.rendersTree) {
            return;
        }

        combineLatest([combinedQuery$, sort$, this.userPaginationInfo$])
            .pipe(
                map(([query, sort, pagination]) => ({ query: query.structuredQuery, sort, pagination })),
                debounceTime(0),
                distinctUntilChanged(isEqual),
                skip(1),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.onCollapseAll();
                // In step with the tree rather than with the page: a plain refresh keeps the expansions it
                // already has and does not re-fetch their children, so forgetting the formats there would
                // leave those rows on a skeleton nothing ever resolves.
                this.stukFormatsService.reset();
            });
    }

    /**
     * The page's own query plus, in a tree, the top-level constraint of that tree. It sits in the
     * external-query slot deliberately: `userQuery$` nulls that slot, so the constraint stays out of the
     * query the search bar shows back to the user, which is what it is — not one of their filters.
     */
    private toPageQuery$(): Observable<ApiQuery | null> {
        if (!this.rendersTree) {
            return this.externalQuery$;
        }

        return combineLatest([this.externalQuery$, this.treeMode$]).pipe(
            map(([externalQuery, mode]) => {
                const topLevelQuery: ApiQuery | null = RecordOverviewContainerComponent.TOP_LEVEL_QUERIES[mode];

                return topLevelQuery
                    ? new ApiQuery(
                          MergeStructuredQueries(
                              topLevelQuery.structuredQuery,
                              externalQuery?.structuredQuery ?? EmptyStructuredQuery
                          )
                      )
                    : externalQuery;
            }),
            distinctUntilChanged(isEqual)
        );
    }

    /**
     * HFBD-75: `Formaten` on a flat page of stukken. A stuk's formats are not on the stuk, so unlike a
     * representatie row — which is one format and answers from its own record — this needs a lookup, asked for
     * the whole page at once and from the page rather than from the rows: subscribing here means the answer is
     * on its way before the cells that read it exist, so they open on a skeleton instead of flashing empty.
     *
     * ⚠ Doing nothing is not an option. The formats state defaults to *loading* for any record it was never
     * asked about — deliberately, since stuk rows only ever existed inside an expansion whose lookup was
     * already in flight — so the untreated symptom is a column of permanent skeletons no user can hide,
     * `Formaten` being pinned.
     *
     * Only asked while a stuk column set is on screen — with the dossier columns there is no `Formaten`
     * column for an answer to land in, and {@link enableNestedStukken} always qualifies. Which rows are asked
     * about is {@link ApiFragment.hasRepresentationFormats}, so weesdocumenten ride along with the stukken:
     * they are stuk-level too, and the warning above is exactly what they would hit otherwise. Representaties
     * are left out because pointing the batched lookup at them returns a *wrong* answer rather than a missing
     * one — see {@link StukFormatsCellComponent}.
     */
    private initFlatStukFormats(): void {
        if (!this.rendersTree) {
            return;
        }

        combineLatest([this.records$.pipe(filter(records => !!records)), this.isStukLevelOnly$])
            .pipe(
                map(([records, stukLevelOnly]) =>
                    stukLevelOnly || this.enableNestedStukken
                        ? (records?.results ?? [])
                              .filter(record => record.hasRepresentationFormats())
                              .map(record => record.getId())
                        : []
                ),
                filter(stukIds => stukIds.length > 0),
                // As {@link initStukCounts} does: the service ignores a repeat of what it has been told, but
                // saying it once per page rather than once per emission is what makes that a safeguard.
                distinctUntilChanged(isEqual),
                takeUntil(this.destroy$)
            )
            .subscribe(stukIds => this.stukFormatsService.load(stukIds));
    }

    /**
     * The sort the user *chose*, in this session or in a previous one — as opposed to the page's own default
     * order, which {@link currentSort$} resolves to and which there is nothing to reset to.
     *
     * The stored half is not redundant with the in-session half: nothing re-seeds {@link sortInfo$} on the way
     * back from a detail page, so a stored sort returns instead as the *default* option
     * {@link initSortOptions} marks — which is why reading `sortInfo$` alone would miss it entirely.
     */
    private chosenSort$(): Observable<SortInfo | null> {
        return combineLatest([this.sortInfo$, this.overViewOptions$]).pipe(
            map(([sortInfo, options]) => sortInfo ?? options?.sort ?? null)
        );
    }

    /**
     * HFBD-75: a sort the incoming column set cannot show falls back to the page's default order. The rule and
     * every operator it rests on live in {@link resetOrphanedSort}; what belongs here is what "reset" means on
     * a record overview.
     *
     * Both the in-session sort **and** the stored per-view sort have to go, since either can be the one on
     * screen (see {@link chosenSort$}). Clearing both is what expresses "use the page default".
     *
     * HFBD-85 keys the guard to {@link rendersGridTable} rather than to the tree, because the flat detail pages
     * get Kolombeheer — so it fires on **picker** changes too, not only on flatten and unflatten.
     *
     * ⚠ It keeps writing the stored sort under the **route** id, not {@link columnOverviewId}. The sort is
     * per-route on purpose; only the column selection moves.
     */
    private initSortReset(): void {
        if (!this.rendersGridTable) {
            return;
        }

        resetOrphanedSort(this.tableColumns$, this.chosenSort$(), () => {
            this.sortInfo$.next(null);
            this.sandbox.setSort(this.getViewId(), undefined);
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe();
    }

    /**
     * HFBD-5: the population the filter panel's facet counts are computed over, which the panel compares a
     * single facet value against to decide the filter cannot narrow anything.
     *
     * Flat, that is the page's own total — the table and the facets run the same query. In a tree it is not:
     * the table query adds a top-level constraint, so the page counts the population itself. Turning the seam
     * off must also stop that extra request, or "restores today's behaviour" would mean today's behaviour plus
     * a search per query.
     */
    private initFacetResultTotal(): void {
        if (!this.hideNonDiscriminatingFacets) {
            this.facetResultTotal$ = of(undefined);

            return;
        }

        const pageTotal$: Observable<number | undefined> = this.records$.pipe(
            map(records => records?.paginationInfo.totalNumberOfResults)
        );

        this.facetResultTotal$ = this.treeMode$.pipe(
            switchMap(mode => (mode === 'flat' ? pageTotal$ : this.countFacetPopulation$())),
            distinctUntilChanged(),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    /**
     * One count-only request over the very query the panel facets on — the URL's structured query plus the
     * facet restrictions, exactly as {@link ShProfileFacetListContainerComponent} builds it. No results are
     * fetched, only the total. Made only in a tree; in a flat list the page's own total is correct.
     */
    private countFacetPopulation$(): Observable<number | undefined> {
        return combineLatest([
            this.urlQueryService.getStructuredQuery(),
            this.facetQueryRestrictions$,
            this.refresh$
        ]).pipe(
            map(([query, restrictions]) => this.luceneHelper.queryToLuceneQuery(new ApiQuery(query, restrictions))),
            distinctUntilChanged(),
            switchMap(luceneQuery =>
                this.sandbox.search({ Q: luceneQuery || undefined, StartIndex: 0, NrOfResults: 0 }).pipe(
                    map(results => results?.paginationInfo.totalNumberOfResults),
                    // A total we could not take is not a total we may guess at: skip the rule instead.
                    catchError(() => of(undefined))
                )
            )
        );
    }

    /** Counts the top-level rows of a tree, dossiers or stukken, rather than objects. */
    private initResultsTitle(): void {
        this.resultsTitle$ = combineLatest([this.records$, this.treeMode$]).pipe(
            map(([records, mode]) => {
                const count: number = records?.paginationInfo.totalNumberOfResults ?? 0;

                const titleKey: string | null = this.enableHierarchy
                    ? RecordOverviewContainerComponent.RESULTS_TITLE_KEYS[mode]
                    : null;

                return titleKey ? this.translateService.instant(titleKey, { items: count }) : this.titleMapperFn(count);
            }),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }
    // #endregion

    private enrichRecord(record: ApiFragment): Observable<ApiFragment> {
        return this.enrichedRecords$.pipe(
            map(enriched => {
                const enrichedRecord: ApiFragment | undefined = enriched.get(record.getId());

                return enrichedRecord ? enrichedRecord : record;
            }),
            distinctUntilChanged(isEqual),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    protected mapParamsToSearch(
        query: ApiQuery | null,
        facetQuery: ApiQuery | null,
        externalQuery: ApiQuery | null,
        queryParams: Params | null,
        sortInfo: SortInfo | null,
        paginationInfo: PaginationInfo | undefined,
        skipSideEffects: boolean = false,
        columnFilterQuery: ApiQuery | null = null
    ): RecordSearch {
        const startIndex: number = this.determineStartIndex(paginationInfo?.page, paginationInfo?.resultsPerPage);

        const queries: ApiQuery[] = [];
        if (query) {
            queries.push(query);
        }

        if (facetQuery) {
            queries.push(facetQuery);
        }

        if (externalQuery) {
            queries.push(externalQuery);
        }

        if (columnFilterQuery) {
            queries.push(columnFilterQuery);
        }

        const luceneQuery: string = this.luceneHelper.queryToLuceneQuery(...queries);

        const recordSearch: RecordSearch = {
            Q: luceneQuery ? luceneQuery : undefined,
            StartIndex: startIndex,
            NrOfResults: paginationInfo?.resultsPerPage || this.defaultPageSize,
            Sort: sortInfo?.field,
            Direction: sortInfo?.direction
        };

        if (!skipSideEffects) {
            //ED-3967: queryparams should be saved to state on queryparams
            const storedQueryParams =
                paginationInfo !== undefined
                    ? {
                          ...queryParams,
                          pageSize: paginationInfo.resultsPerPage,
                          page: paginationInfo.page + 1
                      }
                    : queryParams;
            this.sandbox.setRecordSearch(this.getViewId(), recordSearch);
            this.sandbox.setQueryParams(this.getViewId(), queryParams ?? undefined);
        }
        return recordSearch;
    }

    private determineStartIndex(page?: number, size?: number): number {
        if (page && size) {
            return Number(page) * Number(size);
        } else if (page) {
            return Number(page) * this.defaultPageSize;
        }

        return 0;
    }

    private initEnableSelect(): void {
        // `combineLatest([])` completes without emitting, so on a page with no bulk actions the flag is
        // never delivered rather than `false` — without the `startWith` the OR below inherits that.
        const hasBulkAction$: Observable<boolean> = combineLatest([
            this.currentBulkActionItems$,
            this.recordList$
        ]).pipe(
            map(([bulkActionItems, records]): Observable<ActionItem | null>[] =>
                bulkActionItems.map(
                    (recordActionItem: RecordActionItem<ApiFragment[]>): Observable<ActionItem | null> =>
                        combineLatest(records).pipe(
                            switchMap(recordList => recordActionItem.toActionItem(recordList, this.bulkObserver)),
                            takeUntil(this.destroy$)
                        )
                )
            ),
            switchMap(actionItemObservables => combineLatest(actionItemObservables)),
            map((actionItems: (ActionItem | null)[]) =>
                actionItems.filter((actionItem: ActionItem | null): actionItem is ActionItem => !!actionItem)
            ),
            map(actionItems => actionItems.length > 0),
            startWith(false)
        );

        this.shouldEnableSelect$ = combineLatest([hasBulkAction$, this.enableRowSelect$]).pipe(
            map(([hasBulkAction, enableRowSelect]) => hasBulkAction || enableRowSelect),
            takeUntil(this.destroy$),
            distinctUntilChanged()
        );
    }

    private initActionItems(): void {
        this.rowActionItems$ = combineLatest([this.recordList$, this.currentRecordActionItems$]).pipe(
            takeUntil(this.destroy$),
            filter(([_, actionItems]) => actionItems.length > 0),
            map(
                ([records, actionItems]: [
                    Observable<ApiFragment>[],
                    RecordActionItem<ApiFragment>[]
                ]): Observable<ActionItem>[] =>
                    records.map((record: Observable<ApiFragment>) =>
                        record.pipe(
                            switchMap((recordValue: ApiFragment): Observable<ActionItem> =>
                                this.mapRecordToActionItem(recordValue, actionItems)
                            ),
                            takeUntil(this.destroy$)
                        )
                    )
            )
        );

        this.hierarchyRowActions$ = combineLatest([this.hierarchyRows$, this.currentRecordActionItems$]).pipe(
            takeUntil(this.destroy$),
            filter(([, actionItems]) => actionItems.length > 0),
            map(
                ([rows, actionItems]): ReadonlyMap<string, Observable<ActionItem>> =>
                    new Map(
                        rows
                            // Only record rows: the tree's structural rows carry no record to act on.
                            .filter(row => row.kind === 'record' && !!row.record$)
                            .map(row => [
                                row.key,
                                row.record$!.pipe(
                                    switchMap(record => this.mapRecordToActionItem(record, actionItems)),
                                    takeUntil(this.destroy$)
                                )
                            ])
                    )
            )
        );

        this.actionBarActionItems$ = combineLatest([this.currentBulkActionItems$, this.selectedRecords$]).pipe(
            switchMap(([bulkActionItems, records$]): Observable<Observable<ActionItem | null>[]> =>
                combineLatest(records$).pipe(
                    map(records =>
                        bulkActionItems.map(
                            (recordActionItem: RecordActionItem<ApiFragment[]>): Observable<ActionItem | null> =>
                                recordActionItem.toActionItem(records, this.bulkObserver, ActionItemStyle.Inverted)
                        )
                    ),
                    takeUntil(this.destroy$)
                )
            ),
            takeUntil(this.destroy$),
            shareReplay(1)
        );
    }

    /** HFBD-10: the preview action rides along with every other row action, so it sits inside the same `⋯` menu instead of a button of its own — second, right after the menu's own first entry. */
    private mapRecordToActionItem(
        record: ApiFragment,
        recordActionItems: RecordActionItem<ApiFragment>[]
    ): Observable<ActionItem> {
        return combineLatest([
            combineLatest(
                recordActionItems.map(recordActionItem => recordActionItem.toActionItem(record, this.observer))
            ),
            this.stukFormatsService.formatsFor(record.getId())
        ]).pipe(
            map(([actionItems, formats]: [(ActionItem | null)[], StukFormats]): ActionItem[] => {
                const items: ActionItem[] = actionItems.filter(
                    (actionItem: ActionItem | null): actionItem is ActionItem => !!actionItem
                );
                const previewItem: ActionItem | null = this.toPreviewActionItem(formats);

                return previewItem ? [...items.slice(0, 1), previewItem, ...items.slice(1)] : items;
            }),
            map((actionItems: ActionItem[]) =>
                EllipsisMultiActionItem(actionItems, false, ActionItemStyle.PureSquareIconButton)
            ),
            takeUntil(this.destroy$)
        );
    }

    private initSelectedRecordCountStream(): void {
        this.selectedRecordCount$ = this.selectedRecords$.pipe(
            map(records => records.length),
            takeUntil(this.destroy$)
        );
    }

    private initPagedResult(): void {
        this.pagedResult$ = this.records$.pipe(
            map(paginatedResults => paginatedResults?.paginationInfo ?? null),
            shareReplay(1)
        );

        this.pagedResult$
            .pipe(takeUntil(this.destroy$))
            .subscribe(pagedResult => this.recordsPagedResult.emit(pagedResult));
    }

    private initColumns(): void {
        this.recordColumns$ = combineLatest([this.currentColumnSelection$, this.fieldDefinitions$]).pipe(
            takeUntil(this.destroy$),
            map(([columns, fieldDefinitions]) => this.mapColumnWithFieldDefinitions(columns, fieldDefinitions))
        );

        this.childColumns$ = combineLatest([
            this.currentChildColumnSelection$,
            this.fieldDefinitions$,
            this.treeMode$
        ]).pipe(
            takeUntil(this.destroy$),
            map(([columns, fieldDefinitions, mode]) =>
                columns
                    ? this.mapColumnWithFieldDefinitions(
                          mode === 'dossier'
                              ? columns.filter(column => column.key !== HierarchyColumn.Parent)
                              : columns,
                          fieldDefinitions
                      )
                    : null
            )
        );

        // HFBD-75: a binding, not a rendering rule — the table already picks a column set per row by level, and
        // a flat row is level 0, so it renders whatever this carries. Marking flat rows level 1 instead would
        // run them through the child-row factory, whose `selectable` defaults to false, and silently strip the
        // checkbox, select-all and batch bar from every flat page.
        //
        // The guard is the decision rather than the plumbing: `stukColumns` is null on a condensed/print
        // instance and on an overview that configures no stuk level at all.
        this.tableColumns$ = combineLatest([this.recordColumns$, this.childColumns$, this.isStukLevelOnly$]).pipe(
            map(([dossierColumns, stukColumns, stukLevelOnly]) =>
                stukLevelOnly && stukColumns ? stukColumns : dossierColumns
            ),
            distinctUntilChanged(isEqual),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    /** Skipped while `condensed` (e.g. the print overlay) because a dynamically-created condensed instance can share a route — and thus viewId — with a live, already-configured overview. */
    private initColumnManagement(): void {
        const viewId: string = this.getColumnViewId();
        const isColumnManaged: boolean = !this.condensed && this.columnManagementService.isConfigured(viewId);

        this.initChildColumnManagement();

        this.managedViewIds = isColumnManaged
            ? [viewId, ...(this.isChildColumnManaged() ? [this.getChildViewId()] : [])]
            : [];

        if (!isColumnManaged) {
            return;
        }

        this.columnManagementService
            .getActiveColumns$(viewId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(columns => this.currentColumnSelection$.next(columns));
    }

    /**
     * HFBD-7a: the stuk level, configured by the page under the same view id's child key. Read only while the
     * tree is on — a flat overview renders one level, and a second column set behind rows that can never
     * appear would be state nothing keeps honest.
     */
    private initChildColumnManagement(): void {
        if (!this.isChildColumnManaged()) {
            return;
        }

        this.columnManagementService
            .getActiveColumns$(this.getChildViewId())
            .pipe(takeUntil(this.destroy$))
            .subscribe(columns => this.currentChildColumnSelection$.next(columns));
    }

    /**
     * The key column management reads, which is the only consumer {@link columnOverviewId} overrides. Every
     * other reader of {@link getViewId} is left on the route deliberately — see that input.
     */
    private getColumnViewId(): string {
        return this.columnOverviewId ?? this.getViewId();
    }

    private getChildViewId(): string {
        return GetChildOverviewId(this.getColumnViewId());
    }

    private isChildColumnManaged(): boolean {
        return (
            !this.condensed && this.enableHierarchy && this.columnManagementService.isConfigured(this.getChildViewId())
        );
    }

    private mapColumnWithFieldDefinitions(
        columns: ColumnDef<ApiFragment>[],
        fieldDefinitions: FieldDefinition[]
    ): Column<ApiFragment>[] {
        return columns.flatMap(columnDefinition => {
            const fieldDefinition = fieldDefinitions.find(
                fieldDef =>
                    fieldDef.FlatKey === columnDefinition.sortField || fieldDef.DottedKey === columnDefinition.sortField
            );
            return { columnDefinition, fieldDefinition } as Column<ApiFragment>;
        });
    }

    private initSortOptions(): void {
        this.sortOptions$ = combineLatest([this.currentSortOptions$, this.overViewOptions$]).pipe(
            takeUntil(this.destroy$),
            map(([sortOptions, overviewOptions]) => {
                if (overviewOptions?.sort === undefined) {
                    return sortOptions;
                }
                const sort = overviewOptions.sort;
                return sortOptions.map(sortOption => ({
                    ...sortOption,
                    isDefault: sortOption.direction === sort.direction && sortOption.field === sort.field
                }));
            }),
            distinctUntilChanged(isEqual)
        );
    }

    private initFacets(): void {
        this.facetProfile$ = this.recordTypes$.pipe(
            switchMap(recordTypes => this.sandbox.getFacetProfile(recordTypes, this.hiddenFacetIds, this.facetTag)),
            tap(profile => (this.latestFacetProfile = profile)),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
    }

    private initFacetsListener(): void {
        this.urlQueryService
            .getFacetQueryParts()
            .pipe(takeUntil(this.destroy$), distinctUntilChanged(isEqual))
            .subscribe(facetQueryParts => this.onFacetQueryChange(new ApiQuery(facetQueryParts)));
    }

    private initFacetOrder(): void {
        this.viewId$
            .pipe(takeUntil(this.destroy$))
            .subscribe(viewId => (this.facetOrder = facetOrderForView(this.facetOrderService, viewId)));
    }

    /** HFBD-5: a filter hidden from the panel loses its column-header dropdown too. */
    public onPanelVisibleFacetKeys(keys: string[]): void {
        this.panelVisibleFacetKeys$.next(keys);
    }

    /**
     * Not scoped to a `viewId` — a saved query is organisation-wide, not per-overview, so the same list
     * renders identically on every overview that opts in.
     */
    private initSavedQueries(): void {
        if (!this.enableSavedQueries) {
            return;
        }

        const fetchedSavedQueries$: Observable<SavedQuery[] | null> = this.savedQueriesSandbox
            .getSavedQueries({ startIndex: 0, nrOfResults: 50, sort: 'Name', direction: SortDirection.ASC })
            .pipe(
                map(paginatedResults => paginatedResults.results),
                catchError(() => of(null)),
                shareReplay(1)
            );

        const savedQueries$: Observable<SavedQuery[] | null> = combineLatest([
            fetchedSavedQueries$,
            this.locallySavedQueries$
        ]).pipe(map(([fetched, locallySaved]) => (fetched === null ? null : [...locallySaved, ...fetched])));

        this.savedQueries$ = savedQueries$;
        this.savedQueriesError$ = savedQueries$.pipe(map(queries => queries === null));
        this.savedQueriesTotalCount$ = this.savedQueriesSandbox.getTotalSavedQueries().pipe(catchError(() => of(0)));

        this.activeQueryId$ = this.activatedRoute.queryParams.pipe(
            map(params => params[ParamField.SavedQuery] ?? null),
            distinctUntilChanged()
        );

        this.activeQuery$ = combineLatest([savedQueries$, this.activeQueryId$]).pipe(
            map(
                ([queries, activeQueryId]) => (queries ?? []).find(candidate => candidate.Id === activeQueryId) ?? null
            ),
            shareReplay(1)
        );

        /** Whether the search on screen has anything worth saving — the inline "Huidige zoekopdracht" row's gate. */
        this.canSaveQuery$ = this.urlQueryService
            .getStructuredQuery()
            .pipe(map(structuredQuery => narrowsResults(structuredQuery)));

        const relevantUrlParams$: Observable<QueryComparisonParams> = this.activatedRoute.queryParams.pipe(
            map(toQueryComparisonParams),
            distinctUntilChanged(isEqual)
        );

        // shareReplay(1): bound via 4 separate `| async` pipes in the template (the dot, its tooltip, the
        // icon name and the "changed" class) — without it each one reruns this combineLatest independently.
        this.canUpdateQuery$ = combineLatest([this.activeQuery$, relevantUrlParams$, this.loadedQueryParams$]).pipe(
            map(([active, params, baseline]) => !!active && active.Context.IsEditable && !isEqual(params, baseline)),
            distinctUntilChanged(),
            shareReplay(1)
        );

        this.initLoadedQueryParamsFromUrl();
    }

    private initLoadedQueryParamsFromUrl(): void {
        this.activeQuery$
            .pipe(
                filter((active): active is SavedQuery => !!active),
                take(1),
                takeUntil(this.destroy$)
            )
            .subscribe(active => {
                if (this.loadedQueryParams$.value === null) {
                    this.loadedQueryParams$.next(this.baselineFor(active));
                }
            });
    }

    /**
     * HFBD-165: `savedQueryToStructuredQuery` throws on stored strings the Lucene grammar does not take
     * ("nestedTerm is not yet supported"), which a saved query can hold because saving stores the composed
     * query verbatim. That threw out of the subscribe above, so the baseline stayed `null`, every comparison
     * ran against `null` and the "changed" dot was on for good — a reload included, which is what made it
     * look permanent. Nothing here changes what is stored or how a query is applied: when the string cannot
     * be read back, the url as it stands becomes the baseline, so the dot is off until the user changes
     * something. A dot that is always on is worse than none, and the update control sits next to it either
     * way.
     */
    private baselineFor(active: SavedQuery): QueryComparisonParams {
        try {
            return this.captureLoadedQueryParams(savedQueryToStructuredQuery(this.luceneParser, active.Query));
        } catch {
            return toQueryComparisonParams(this.activatedRoute.snapshot.queryParams);
        }
    }

    /**
     * HFBD-165: re-applies a query that was just saved or updated, and makes sure the "changed" dot goes out
     * whether or not that succeeds. `onLoadSavedQuery` parses the stored string first, so on a string the
     * grammar rejects it threw before it could refresh the baseline, and the dot kept comparing against what
     * it held before the save. Nothing was re-applied in that case, which is exactly why the url on screen
     * *is* what was just written and can serve as the baseline. The successful path is untouched: there the
     * baseline comes from the navigation {@link onLoadSavedQuery} performs.
     */
    private reapplyJustWrittenQuery(query: SavedQuery): void {
        try {
            this.onLoadSavedQuery(query);
        } catch {
            this.loadedQueryParams$.next(toQueryComparisonParams(this.activatedRoute.snapshot.queryParams));
        }
    }

    private currentSavedQueryId(): string | null {
        return this.activatedRoute.snapshot.queryParams[ParamField.SavedQuery] ?? null;
    }

    /**
     * The `q`/`advQ`/`f` baseline for {@link canUpdateQuery$} right after applying `structuredQuery` — `q`
     * and `advQ` are computed the exact same way {@link UrlQueryService.setStructuredQuery} turns it into
     * URL params, so the baseline matches the URL once the navigation settles without waiting for it or
     * re-parsing anything back out of it.
     *
     * HFBD-165: `f` (facets) is `undefined` for the same reason, and never the snapshot's current value.
     * {@link UrlQueryService.setStructuredQuery} hardcodes the facet param to `undefined` and navigates
     * with `queryParamsHandling: 'merge'`, which *drops* it — so applying a query always leaves the URL
     * without facets. Baselining the pre-navigation `f` recorded a value the navigation was about to
     * delete, and with any facet selected the dot then stayed lit forever. Facets a user picks *after*
     * the query was applied are still drift, which is why `f` remains part of the comparison.
     */
    private captureLoadedQueryParams(structuredQuery: StructuredQuery): QueryComparisonParams {
        const generatedParams: UrlQueryParams = UrlQueryParamsConverter.generateQueryParams(structuredQuery);

        return {
            q: generatedParams.q,
            advQ: generatedParams.advQ,
            f: undefined
        };
    }

    /** Loads a saved query (server-side, groups-shared) into the active search, the same way as the search bar's own "load" flow. */
    public onLoadSavedQuery(query: SavedQuery): void {
        const structuredQuery: StructuredQuery = savedQueryToStructuredQuery(this.luceneParser, query.Query);

        this.urlQueryService.setStructuredQuery(structuredQuery, query.Id);
        this.loadedQueryParams$.next(this.captureLoadedQueryParams(structuredQuery));
    }

    /**
     * Opens the same {@link SaveQueryDialogComponent} the search bar's own "Bewaar zoekopdracht" action uses,
     * for the inline "Huidige zoekopdracht" row that shows once there's nothing active to update instead.
     */
    public onSaveSavedQuery(): void {
        this.currentQuery$.pipe(take(1), takeUntil(this.destroy$)).subscribe(cleanedQuery => {
            this.dialogService
                .open<SavedQuery>(SaveQueryDialogComponent, {
                    title: this.translateService.instant('components.saved-queries.new-saved-query-dialog.title'),
                    data: cleanedQuery,
                    options: {
                        style: DialogStyle.Wide
                    }
                })
                .afterClosed$.pipe(
                    take(1),
                    filter((created): created is SavedQuery => !!created),
                    takeUntil(this.destroy$)
                )
                .subscribe(created => {
                    this.locallySavedQueries$.next([created, ...this.locallySavedQueries$.value]);
                    // Re-applies the query — this is also what clears the "changed" dot (via onLoadSavedQuery),
                    // rather than just marking `created` active without doing so.
                    this.reapplyJustWrittenQuery(created);
                });
        });
    }

    /** Opens the same {@link LoadSavedQueryDialogComponent} the search bar's own "load" action uses, for the inline "gebruiken" link. */
    public onOpenLoadSavedQueryDialog(): void {
        const title: string = this.translateService.instant('components.saved-queries.load-saved-query-dialog.title');
        const cancel: string = this.translateService.instant('actions.common.cancel');
        const search: string = this.translateService.instant('actions.common.search');

        this.dialogService
            .open<LoadSavedQueryDialogOutput>(LoadSavedQueryDialogComponent, {
                title,
                data: { cancel, search },
                options: {
                    style: DialogStyle.Wide
                }
            })
            .afterClosed$.pipe(
                take(1),
                filter((output): output is LoadSavedQueryDialogOutputSuccess => !!output && output.type === 'success'),
                takeUntil(this.destroy$)
            )
            .subscribe(output => this.onLoadSavedQuery(output.query));
    }

    /**
     * Overwrites `query`'s stored `Query` with what's on screen (ticket 07) — confirms first, since there
     * is no undo once the PUT lands. Also lets the user change who it's shared with, since the confirm
     * step is already interrupting them for this query.
     */
    public onUpdateSavedQuery(query: SavedQuery): void {
        if (!query.Context.IsEditable) {
            return;
        }

        this.currentQuery$.pipe(take(1), takeUntil(this.destroy$)).subscribe(currentQuery => {
            this.dialogService
                .open<SavedQuery>(UpdateQueryDialogComponent, {
                    title: this.translateService.instant('components.saved-queries.update-confirm-dialog.title'),
                    data: { query, currentQuery },
                    options: {
                        style: DialogStyle.Wide
                    }
                })
                .afterClosed$.pipe(
                    take(1),
                    filter((updated): updated is SavedQuery => !!updated),
                    takeUntil(this.destroy$)
                )
                .subscribe(updated => {
                    this.locallySavedQueries$.next([updated, ...this.locallySavedQueries$.value]);
                    this.reapplyJustWrittenQuery(updated);
                });
        });
    }

    public onDeactivateSavedQuery(): void {
        this.resetActiveQuery();
    }

    private resetActiveQuery(): void {
        this.urlQueryService.setStructuredQuery(EmptyStructuredQuery, null);
        this.loadedQueryParams$.next(this.captureLoadedQueryParams(EmptyStructuredQuery));
    }

    /** Same gate and confirm-then-delete flow as {@link DeleteSavedQueryActionCreator} on the saved-queries overview page. */
    public onDeleteSavedQuery(query: SavedQuery): void {
        if (!query.Context.IsDeletable) {
            return;
        }

        this.dialogService
            .open<boolean>(ShBooleanDialogComponent, {
                title: this.translateService.instant('actions.common.delete'),
                data: {
                    yes: this.translateService.instant('actions.common.delete'),
                    no: this.translateService.instant('actions.common.cancel'),
                    message: this.translateService.instant('components.saved-queries.delete-confirm-dialog.message'),
                    warningMessage:
                        query.Groups.length > 0
                            ? this.translateService.instant(
                                  'components.saved-queries.delete-confirm-dialog.shared-warning'
                              )
                            : undefined
                }
            })
            .afterClosed$.pipe(
                take(1),
                filter(confirmed => confirmed),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                const toast: Toast = this.savedQueriesSandbox.addDeletingToast();

                this.savedQueriesSandbox
                    .deleteQuery(query.Id)
                    .pipe(take(1), takeUntil(this.destroy$))
                    .subscribe({
                        next: () => {
                            if (this.currentSavedQueryId() === query.Id) {
                                this.resetActiveQuery();
                            }

                            this.savedQueriesSandbox.updateDeletingToastSuccess(toast.id);
                        },
                        error: () => this.savedQueriesSandbox.updateDeletingToastFailure(toast.id)
                    });
            });
    }

    private initColumnFilterListener(): void {
        this.columnFilters$ = this.activeFilterState.getColumnFilters();

        this.columnFilters$.pipe(takeUntil(this.destroy$)).subscribe(queryParts => {
            this.onColumnFilterQueryChange(new ApiQuery({ ...EmptyStructuredQuery, ColumnFilters: queryParts }));
        });
    }

    private initFacetFilterState(): void {
        // The selected facet values, grouped by field, that pre-fill the dropdowns and drive their active indicator.
        this.activeFacetFilters$ = this.urlQueryService.getSelectedFacets().pipe(
            map(selectedFacets => groupFacetsByField(selectedFacets)),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );

        this.enumFacetFieldKeys$ = combineLatest([
            this.facetProfile$.pipe(map(profile => (profile.Fields ?? []).map(field => field.DottedKey))),
            this.panelVisibleFacetKeys$,
            this.activeFacetFilters$
        ]).pipe(
            map(([profileKeys, visibleKeys, activeFilters]) =>
                toEnumFacetFieldKeys(profileKeys, visibleKeys, activeFilters)
            ),
            distinctUntilChanged(isEqual)
        );

        // The same query (incl. page restrictions) the side panel uses, so column buckets match the left filters.
        this.facetApiQuery$ = combineLatest([
            this.urlQueryService.getStructuredQuery(),
            this.facetQueryRestrictions$
        ]).pipe(
            map(([structuredQuery, restrictions]) => new ApiQuery(structuredQuery, restrictions)),
            takeUntil(this.destroy$)
        );

        this.enumFacets$ = this.enumFilter.facets$.pipe(takeUntil(this.destroy$));
    }

    /**
     * Facet-origin pills for the active-filter bar: one translated primary pill per selected facet value.
     * Runs after {@link initFacets} and {@link initFieldDefinitions} so both source streams exist.
     */
    private initActiveFilterPills(): void {
        this.activeFilterPills$ = this.activeFilterState
            .getPills$({
                facetProfile$: this.facetProfile$,
                fieldDefinitions$: this.fieldDefinitions$,
                columns$: this.recordColumns$,
                submapFacetKey: this.submapFacetKey
            })
            .pipe(takeUntil(this.destroy$));
    }

    private initFieldDefinitions(): void {
        this.fieldDefinitionsLoading$.startLoading();
        this.fieldDefinitions$ = this.sandbox.getFieldDefinitions().pipe(
            takeUntil(this.destroy$),
            shareReplay(),
            tap(fieldDefinitions => (this.latestFieldDefinitions = fieldDefinitions)),
            tap(() => this.fieldDefinitionsLoading$.stopLoading())
        );
    }

    private initRecords(): void {
        const sort$: Observable<SortInfo | null> = combineLatest([this.sortInfo$, this.sortOptions$]).pipe(
            map(([sortInfo, sortOptions]) => {
                if (sortInfo !== null) {
                    return sortInfo;
                }
                const defaultSortOption = sortOptions.find(option => option.isDefault);
                if (defaultSortOption === undefined) {
                    return null;
                }
                return {
                    direction: defaultSortOption.direction,
                    field: defaultSortOption.field
                };
            })
        );

        this.currentSort$ = sort$.pipe(
            map(sort => sort ?? { field: '', direction: SortDirection.ASC }),
            shareReplay(1)
        );

        sort$
            .pipe(
                takeUntil(this.destroy$),
                distinctUntilChanged(isEqual),
                filter(sort => sort!)
            )
            .subscribe(sortInto => this.sortChanged.next(sortInto));

        const pageQuery$: Observable<ApiQuery | null> = this.toPageQuery$();

        const combinedQuery$: Observable<ApiQuery> = combineLatest([
            this.searchBarQuery$,
            this.facetQuery$,
            pageQuery$,
            this.columnFilterQuery$
        ]).pipe(
            takeUntil(this.destroy$),
            map(([query, facetQuery, externalQuery, columnFilterQuery]) => {
                let structuredQuery: StructuredQuery = EmptyStructuredQuery;
                if (query) {
                    structuredQuery = MergeStructuredQueries(query.structuredQuery, structuredQuery);
                }

                if (facetQuery) {
                    structuredQuery = MergeStructuredQueries(facetQuery.structuredQuery, structuredQuery);
                }

                if (externalQuery) {
                    structuredQuery = MergeStructuredQueries(externalQuery.structuredQuery, structuredQuery);
                }

                if (columnFilterQuery) {
                    structuredQuery = MergeStructuredQueries(columnFilterQuery.structuredQuery, structuredQuery);
                }

                return new ApiQuery(structuredQuery);
            }),
            distinctUntilChanged(isEqual)
        );

        const internalSources$: Observable<any[]> = combineLatest([
            this.searchBarQuery$,
            this.facetQuery$,
            pageQuery$,
            this.activatedRoute.queryParams,
            sort$,
            this.userPaginationInfo$,
            this.columnFilterQuery$
        ]).pipe(shareReplay(1));

        const internalRecordSearch$: Observable<RecordSearch> = internalSources$.pipe(
            takeUntil(this.destroy$),
            map(([query, facetQuery, externalQuery, params, sortInfo, pageAndSize, columnFilterQuery]) =>
                this.mapParamsToSearch(
                    query,
                    facetQuery,
                    externalQuery,
                    params,
                    sortInfo,
                    pageAndSize,
                    false,
                    columnFilterQuery
                )
            ),
            distinctUntilChanged(isEqual)
        );

        this.batchActionItems$ = combineLatest([combinedQuery$, this.batchActionItemsFactories$]).pipe(
            // Sometimes published overview page (parent component) is too slow to feed data into batchActionItemsFactories$
            //  It then makes batchActionItemsFactories$ to be null. Hence, use filter to guard against
            filter(([ApiQuery, batchActionItemFactory]) => !!batchActionItemFactory),
            switchMap(([query, factories]) => createBatchActionItems(factories, query, this.bulkObserver)),
            map(items => items.map(item => ({ style: ActionItemStyle.Inverted, ...item }))),
            shareReplay(1)
        );

        const recordSearch$: Observable<RecordSearch> = combineLatest([internalRecordSearch$, this.refresh$]).pipe(
            map(([search]) => search),
            takeUntil(this.destroy$)
        );

        // RecordSearch without restrictions (only user filters)
        this.userQuery$ = internalSources$.pipe(
            takeUntil(this.destroy$),
            map(([query, facetQuery, externalQuery, params, sortInfo, pageAndSize, columnFilterQuery]) =>
                this.mapParamsToSearch(query, facetQuery, null, params, sortInfo, pageAndSize, true, columnFilterQuery)
            ),
            distinctUntilChanged(isEqual)
        );

        this.currentQuery$ = this.userQuery$.pipe(map(recordSearch => recordSearch.Q?.replace('*:*', '') ?? ''));

        this.records$ = recordSearch$.pipe(
            takeUntil(this.destroy$),
            tap(() => this.recordsLoading$.startLoading()),
            tap(() => this.setError(false)),
            switchMap(search => this.sandbox.search(search).pipe(catchError(err => this.handleSearchFailed(err)))),
            tap(() => this.recordsLoading$.stopLoading()),
            finalize(() => this.recordsLoading$.stopLoading()),
            shareReplay(1)
        );

        combinedQuery$.pipe(takeUntil(this.destroy$), distinctUntilChanged(isEqual)).subscribe(value => {
            this.clearBatchSelection();
            this.queryChanged.next(value);
        });

        this.initExpansionReset(combinedQuery$, sort$);
    }

    handleSearchFailed(err: any): Observable<null> {
        this.setError(true);
        this.recordsLoading$.stopLoading();
        // The table is destroyed when there are no records, taking the selection it holds with it.
        this.onSelectionChange([]);

        return of(null);
    }

    paginationInfoChanged(paginationInfo: PaginationInfo) {
        super.paginationInfoChanged(paginationInfo);
    }

    private initCombinedLoading(): void {
        IsOneStreamLoading([this.recordsLoading$, this.fieldDefinitionsLoading$]).subscribe(isLoading =>
            this.loading$.next(isLoading)
        );
    }

    private initViewId(): void {
        this.viewId$ = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.getViewId()),
            distinctUntilChanged(),
            // Before the share: `router.events` never completes, so it is the only thing that ends the source.
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        // Synchronous on subscribe, so the first push still lands before the rest of `ngOnInit` runs.
        let previous: string | undefined;
        this.viewId$.pipe(takeUntil(this.destroy$)).subscribe(viewId => {
            if (previous === undefined) {
                this.sandbox.pushToStack(viewId, this.topLevelOverview);
            } else {
                this.sandbox.replaceOnStack(previous, viewId, this.topLevelOverview);
            }
            previous = viewId;
        });
    }

    private initOverviewOptions(): void {
        this.overViewOptions$ = this.viewId$.pipe(switchMap(viewId => this.sandbox.getOverviewOptions(viewId)));
    }

    private mapCountToTitle(count: number): string {
        return this.translateService.instant('all-records.results', { items: count });
    }

    get observer(): Observer<void> {
        return {
            next: () => this.refresh$.next(),
            complete: () => {},
            error: () => {}
        };
    }

    get bulkObserver(): Observer<void> {
        return {
            next: () => {
                this.onSelectionChange([]);
                this.selectAll(false);
                // Left in place: the flat list's own `resetSelection` input, which predates `selectAll`.
                this.clearSelection$.next();
            },
            complete: () => {
                this.refresh$.next();
            },
            error: err => {
                console.error(err);
            }
        };
    }

    public getViewId(): string {
        return GetOverviewId(this.activatedRoute);
    }

    get facetSubject(): string {
        return 'records';
    }
}
